/**
 * Root Color Palette
 *
 * This file contains the base color definitions extracted from the design system.
 * These are the raw color values that semantic tokens reference.
 *
 * Colors are organized by category:
 * - color: Primary colors (blue, green, red, orange, purple, magenta, cyan, teal)
 * - neutrals colors
 * - old pounce tokens (temporary, to be removed after complete transfer to new token system)
 */

export const palette = {
  color: {
    blue: {
      brandPrimaryA: {
        0: '#141c2a00',
        25: '#141c2a05',
        50: '#141c2a0a',
        100: '#141c2a14',
        200: '#141c2a1f',
        300: '#141c2a33',
        400: '#141c2a4d',
        500: '#141c2a66',
        600: '#141c2a80',
        700: '#141c2a99',
        800: '#141c2ab2',
        900: '#141c2acc',
        950: '#141c2ae5',
        1000: '#141c2a',
      },
      navy: {
        50: '#afd5ff',
        100: '#86a3c3',
        200: '#4f627c',
        300: '#3e516b',
        400: '#30415c',
        500: '#23344e',
        600: '#1d2a3f',
        700: '#192231',
        800: '#141c2a',
        850: '#121a27',
        900: '#0f1623',
        950: '#0c121c',
        1000: '#0b0f19',
      },
      blue: {
        50: '#afd5ff',
        100: '#ace2ff',
        200: '#7fd3ff',
        300: '#11a5f3',
        400: '#0081c5',
        500: '#0b5297',
        600: '#073e73',
        700: '#042e57',
        800: '#032241',
        900: '#00182e',
        950: '#000d26',
        1000: '#0f1114',
      },
      blueA: {
        50: '#0322410a',
        100: '#03224114',
        200: '#0322411f',
        300: '#03224133',
        400: '#0322414d',
        500: '#03224166',
        600: '#03224180',
        700: '#03224199',
        800: '#032241b2',
        900: '#032241cc',
        950: '#032241e5',
        1000: '#032241',
      },
      blueO: {
        50: '#f4f5f6',
        100: '#eaecef',
        200: '#e0e4e7',
        300: '#ccd2d8',
        400: '#b3bcc5',
        500: '#9aa6b2',
        600: '#8190a0',
        700: '#677a8d',
        800: '#4e647a',
        900: '#354e67',
        950: '#1c3854',
        1000: '#032241',
        1100: '#021425',
        1200: '#00050a',
      },
      astralSabre: {
        50: '#d1e3ff',
        100: '#93b5ff',
        200: '#5471be',
        300: '#5f7dcc',
        400: '#4662ae',
        500: '#38529c',
        600: '#2b438d',
        700: '#20377f',
        800: '#162a71',
        900: '#0c1c62',
        950: '#050f45',
        1000: '#030a35',
      },
      stellarAbyssA: {
        50: '#050f450a',
        100: '#050f4514',
        200: '#050f451f',
        300: '#050f4533',
        400: '#050f454d',
        500: '#050f4566',
        600: '#050f4580',
        700: '#050f4599',
        800: '#050f45b2',
        900: '#050f45cc',
        950: '#050f45e5',
        1000: '#050f45',
      },
      skyboltA: {
        50: '#1c90e10a',
        100: '#1c90e114',
        200: '#1c90e11f',
        300: '#1c90e133',
        400: '#1c90e14d',
        500: '#1c90e166',
        600: '#1c90e180',
        700: '#1c90e199',
        800: '#1c90e1b2',
        900: '#1c90e1cc',
        950: '#1c90e1e5',
        1000: '#1c90e1',
      },
      hullDaylightA: {
        50: '#2235450a',
        100: '#22354514',
        200: '#2235451f',
        300: '#22354533',
        400: '#2235454d',
        500: '#22354566',
        600: '#22354580',
        700: '#22354599',
        800: '#223545b2',
        900: '#223545cc',
        950: '#223545e5',
        1000: '#223545',
      },
      lightA: {
        0: '#0281c400',
        50: '#0281c40a',
        100: '#0281c414',
        200: '#0281c41f',
        300: '#0281c433',
        400: '#0281c44d',
        500: '#0281c466',
        600: '#0281c480',
        700: '#0281c499',
        800: '#0281c4b2',
        900: '#0281c4cc',
        950: '#0281c4e5',
        1000: '#0281c4',
      },
      secondaryBlueA: {
        0: '#0a529700',
        50: '#0a52970a',
        100: '#0a529714',
        200: '#0a52971f',
        300: '#0a529733',
        400: '#0a52974d',
        500: '#0a529766',
        600: '#0a529780',
        700: '#0a529799',
        800: '#0a5297b2',
        900: '#0a5297cc',
        950: '#0a5297e5',
        1000: '#0a5297',
      },
      electricBlueA: {
        0: '#0b529700',
        50: '#0b52970a',
        100: '#0b529714',
        200: '#0b52971f',
        300: '#0b529733',
        400: '#0b52974d',
        500: '#0b529766',
        600: '#0b529780',
        700: '#0b529799',
        800: '#0b5297b2',
        900: '#0b5297cc',
        950: '#0b5297e5',
        1000: '#0b5297',
      },
      neptuneA: {
        0: '#7fd3ff00',
        50: '#7fd3ff0a',
        100: '#7fd3ff14',
        200: '#7fd3ff1f',
        300: '#7fd3ff33',
        400: '#7fd3ff4d',
        500: '#7fd3ff66',
        600: '#7fd3ff80',
        700: '#7fd3ff99',
        800: '#7fd3ffb2',
        900: '#7fd3ffcc',
        950: '#7fd3ffe5',
        1000: '#7fd3ff',
      },
      dockingBaySkyA: {
        50: '#afc4db0a',
        100: '#afc4db14',
        200: '#afc4db1f',
        300: '#afc4db33',
        400: '#afc4db4d',
        500: '#afc4db66',
        600: '#afc4db80',
        700: '#afc4db99',
        800: '#afc4dbb2',
        900: '#afc4dbcc',
        950: '#afc4dbe5',
        1000: '#afc4db',
      },
      ionCurrentBlueA: {
        0: '#0088ff00',
        50: '#0088ff0a',
        100: '#0088ff14',
        200: '#0088ff1f',
        300: '#0088ff33',
        400: '#0088ff4d',
        500: '#0088ff66',
        600: '#0088ff80',
        700: '#0088ff99',
        800: '#0088ffb2',
        900: '#0088ffcc',
        950: '#0088ffe5',
        1000: '#0088ff',
      },
      upperAtmosphereA: {
        50: '#c8e4fd0a',
        100: '#c8e4fd14',
        200: '#c8e4fd1f',
        300: '#c8e4fd33',
        400: '#c8e4fd4d',
        500: '#c8e4fd66',
        600: '#c8e4fd80',
        700: '#c8e4fd99',
        800: '#c8e4fdb2',
        900: '#c8e4fdcc',
        950: '#c8e4fde5',
        1000: '#c8e4fd',
      },
      orbitalTideBlueA: {
        50: '#2a5a8a0a',
        100: '#2a5a8a14',
        200: '#2a5a8a1f',
        300: '#2a5a8a33',
        400: '#2a5a8a4d',
        500: '#2a5a8a66',
        600: '#2a5a8a80',
        700: '#2a5a8a99',
        800: '#2a5a8ab2',
        900: '#2a5a8acc',
        950: '#2a5a8ae5',
        1000: '#2a5a8a',
      },
      clearOrbitBlueA: {
        50: '#0066cc0a',
        100: '#0066cc14',
        200: '#0066cc1f',
        300: '#0066cc33',
        400: '#0066cc4d',
        500: '#0066cc66',
        600: '#0066cc80',
        700: '#0066cc99',
        800: '#0066ccb2',
        900: '#0066cccc',
        950: '#0066cce5',
        1000: '#0066cc',
      },
      stratosphereBlueA: {
        50: '#0066b30a',
        100: '#0066b314',
        200: '#0066b31f',
        300: '#0066b333',
        400: '#0066b34d',
        500: '#0066b366',
        600: '#0066b380',
        700: '#0066b399',
        800: '#0066b3b2',
        900: '#0066b3cc',
        950: '#0066b3e5',
        1000: '#0066b3',
      },
      skyblueO: {
        50: '#daf0ff',
        100: '#bee0ff',
        200: '#a3d1fa',
        300: '#87c1f4',
        400: '#69b1ee',
        500: '#49a0e8',
        600: '#1c90e1',
        700: '#0076bf',
        800: '#005e9e',
        900: '#00467e',
        950: '#002f60',
        1000: '#001a43',
        1100: '#000528',
      },
      neptuneO: {
        50: '#f9fcfe',
        100: '#f4fbfe',
        200: '#eff9fe',
        300: '#e5f5fe',
        400: '#d8f1fe',
        500: '#cbedfe',
        600: '#bfe9ff',
        700: '#b2e4ff',
        800: '#a5e0ff',
        900: '#98dcff',
        950: '#8cd7ff',
        1000: '#7fd3ff',
        1100: '#49c0ff',
        1200: '#13aeff',
        1300: '#0090dc',
        1400: '#006da6',
        1500: '#00496f',
        1600: '#002639',
      },
      lightO: {
        50: '#f4f9fc',
        100: '#eaf4f9',
        200: '#e0eff7',
        300: '#cce5f2',
        400: '#b2d9ed',
        500: '#99cce7',
        600: '#80c0e1',
        700: '#67b3db',
        800: '#4ea7d5',
        900: '#349ad0',
        950: '#1b8eca',
        1000: '#0281c4',
        1100: '#02669a',
        1200: '#014a71',
        1300: '#012f47',
        1400: '#00131d',
      },
      secondaryBlueO: {
        50: '#f4f7fa',
        100: '#eaf0f6',
        200: '#e1e9f2',
        300: '#cddce9',
        400: '#b5cadf',
        500: '#9cb9d5',
        600: '#84a8cb',
        700: '#6c97c0',
        800: '#5386b6',
        900: '#3b74ac',
        950: '#2263a1',
        1000: '#0a5297',
        1100: '#073b6c',
        1200: '#042441',
        1300: '#020c17',
      },
      brandPrimaryO: {
        50: '#f5f5f6',
        100: '#ebeced',
        200: '#e2e3e5',
        300: '#cfd1d4',
        400: '#b8babe',
        500: '#a0a4a9',
        600: '#898d94',
        700: '#72767f',
        800: '#5a606a',
        900: '#434954',
        950: '#2b333f',
        1000: '#141c2a',
        1100: '#0c1018',
        1200: '#030406',
      },
      clearOrbitBlueO: {
        50: '#f4f8fc',
        100: '#eaf2fa',
        200: '#e0ecf8',
        300: '#cbe0f4',
        400: '#b2d0ef',
        500: '#98c1ea',
        600: '#7fb2e5',
        700: '#66a3e0',
        800: '#4c94db',
        900: '#3384d6',
        950: '#1975d1',
        1000: '#0066cc',
        1100: '#0050a1',
        1200: '#003b75',
        1300: '#00254a',
        1400: '#000f1f',
      },
      dockingBaySkyO: {
        50: '#fbfcfd',
        100: '#f8f9fb',
        200: '#f5f7fa',
        300: '#eef2f7',
        400: '#e6edf4',
        500: '#dee7f0',
        600: '#d7e1ed',
        700: '#cfdbe9',
        800: '#c7d5e6',
        900: '#bfd0e2',
        950: '#b7cadf',
        1000: '#afc4db',
        1100: '#89a8ca',
        1200: '#628bb8',
        1300: '#466f9c',
        1400: '#355476',
        1500: '#24384f',
        1600: '#121d29',
      },
      electricBlueO: {
        50: '#f4f7fa',
        100: '#ebf0f6',
        200: '#e1e9f2',
        300: '#cddce9',
        400: '#b5cadf',
        500: '#9db9d5',
        600: '#85a8cb',
        700: '#6c97c0',
        800: '#5486b6',
        900: '#3c74ac',
        950: '#2363a1',
        1000: '#0b5297',
        1100: '#083b6c',
        1200: '#052441',
        1300: '#020c17',
      },
      hullDaylightO: {
        50: '#f5f6f7',
        100: '#eceeef',
        200: '#e4e6e8',
        300: '#d2d6d9',
        400: '#bcc2c7',
        500: '#a6aeb4',
        600: '#909aa2',
        700: '#7a858f',
        800: '#64717d',
        900: '#4e5d6a',
        950: '#384958',
        1000: '#223545',
        1100: '#182631',
        1200: '#0f171e',
        1300: '#05080a',
      },
      ionCurrentBlueO: {
        50: '#f4f9fe',
        100: '#eaf5fe',
        200: '#e0f0fe',
        300: '#cbe6fe',
        400: '#b2dbfe',
        500: '#98cffe',
        600: '#7fc3ff',
        700: '#66b7ff',
        800: '#4cabff',
        900: '#33a0ff',
        950: '#1994ff',
        1000: '#0088ff',
        1100: '#0071d4',
        1200: '#005aa8',
        1300: '#00437d',
        1400: '#002c52',
        1500: '#001426',
      },
      orbitalTideBlueO: {
        50: '#f6f7f9',
        100: '#edf1f5',
        200: '#e5eaf0',
        300: '#d4dde7',
        400: '#becddb',
        500: '#a9bcd0',
        600: '#94acc4',
        700: '#7f9cb8',
        800: '#6a8bad',
        900: '#547ba1',
        950: '#3f6a96',
        1000: '#2a5a8a',
        1100: '#21476d',
        1200: '#18344f',
        1300: '#0f2132',
        1400: '#060e15',
      },
      skyboltO: {
        50: '#f5fafd',
        100: '#ecf5fc',
        200: '#e3f1fb',
        300: '#d1e8f8',
        400: '#baddf5',
        500: '#a4d2f2',
        600: '#8dc7f0',
        700: '#76bced',
        800: '#60b1ea',
        900: '#49a6e7',
        950: '#339be4',
        1000: '#1c90e1',
        1100: '#1778bb',
        1200: '#125f94',
        1300: '#0e476e',
        1400: '#092e48',
        1500: '#041622',
      },
      stellarAbyssO: {
        50: '#f4f4f7',
        100: '#eaebef',
        200: '#e0e1e8',
        300: '#ccced9',
        400: '#b3b6c7',
        500: '#9a9eb4',
        600: '#8287a2',
        700: '#696f8f',
        800: '#50577d',
        900: '#373f6a',
        950: '#1e2758',
        1000: '#050f45',
        1100: '#01020a',
      },
      stratosphereBlueO: {
        50: '#f4f8fb',
        100: '#eaf2f8',
        200: '#e0ecf5',
        300: '#cbe0ef',
        400: '#b2d0e8',
        500: '#98c1e0',
        600: '#7fb2d9',
        700: '#66a3d1',
        800: '#4c94ca',
        900: '#3384c2',
        950: '#1975bb',
        1000: '#0066b3',
        1100: '#00508d',
        1200: '#003b67',
        1300: '#002541',
        1400: '#000f1b',
      },
      upperAtmosphereO: {
        50: '#fcfdfe',
        100: '#fafcfe',
        200: '#f8fbfe',
        300: '#f3f9fe',
        400: '#eef6fe',
        500: '#e8f4fe',
        600: '#e3f1fe',
        700: '#deeefd',
        800: '#d8ecfd',
        900: '#d3e9fd',
        950: '#cde7fd',
        1000: '#c8e4fd',
        1100: '#8ac6fb',
        1200: '#4ca7f8',
        1300: '#0e89f6',
        1400: '#0767bd',
        1500: '#05467f',
        1600: '#022442',
      },
    },
    green: {
      emeraldSaberA: {
        50: '#00c46f0a',
        100: '#00c46f14',
        200: '#00c46f1f',
        300: '#00c46f33',
        400: '#00c46f4d',
        500: '#00c46f66',
        600: '#00c46f80',
        700: '#00c46f99',
        800: '#00c46fb2',
        900: '#00c46fcc',
        950: '#00c46fe5',
        1000: '#00c46f',
      },
      ionSprout: {
        50: '#f4fae5',
        100: '#c9e3a1',
        200: '#acd46e',
        300: '#98c356',
        400: '#67b52c',
        500: '#50931d',
        600: '#25470c',
        700: '#183105',
        800: '#152707',
        900: '#0e1a05',
        950: '#0c1a02',
        1000: '#060d01',
      },
      ionSproutA: {
        50: '#8ed0000a',
        100: '#8ed00014',
        200: '#8ed0001f',
        300: '#8ed00033',
        400: '#8ed0004d',
        500: '#8ed00066',
        600: '#8ed00080',
        700: '#8ed00099',
        800: '#8ed000b2',
        900: '#8ed000cc',
        950: '#8ed000e5',
        1000: '#8ed000',
      },
      verdantFlux: {
        50: '#eef8e9',
        100: '#d5efc8',
        200: '#aadf91',
        300: '#80ce5b',
        400: '#55be24',
        500: '#469d1d',
        600: '#377c16',
        700: '#275c10',
        800: '#183b09',
        900: '#091a02',
        950: '#040d01',
        1000: '#020500',
      },
      verdantFluxA: {
        50: '#55be240a',
        100: '#55be2414',
        200: '#55be241f',
        300: '#55be2433',
        400: '#55be244d',
        500: '#55be2466',
        600: '#55be2480',
        700: '#55be2499',
        800: '#55be24b2',
        900: '#55be24cc',
        950: '#55be24e5',
        1000: '#55be24',
      },
      forestA: {
        0: '#33660c00',
        50: '#33660c0a',
        100: '#33660c14',
        200: '#33660c1f',
        300: '#33660c33',
        400: '#33660c4d',
        500: '#33660c66',
        600: '#33660c80',
        700: '#33660c99',
        800: '#33660cb2',
        900: '#33660ccc',
        950: '#33660ce5',
        1000: '#33660c',
      },
      natureA: {
        0: '#33660c00',
        50: '#33660c0a',
        100: '#33660c14',
        200: '#33660c1f',
        300: '#33660c33',
        400: '#33660c4d',
        500: '#33660c66',
        600: '#33660c80',
        700: '#33660c99',
        800: '#33660cb2',
        900: '#33660ccc',
        950: '#33660ce5',
        1000: '#33660c',
      },
      biolumeKelpA: {
        0: '#3ad90000',
        50: '#3ad9000a',
        100: '#3ad90014',
        200: '#3ad9001f',
        300: '#3ad90033',
        400: '#3ad9004d',
        500: '#3ad90066',
        600: '#3ad90080',
        700: '#3ad90099',
        800: '#3ad900b2',
        900: '#3ad900cc',
        950: '#3ad900e5',
        1000: '#3ad900',
      },
      planktonDriftA: {
        0: '#80ffbb00',
        50: '#80ffbb0a',
        100: '#80ffbb14',
        200: '#80ffbb1f',
        300: '#80ffbb33',
        400: '#80ffbb4d',
        500: '#80ffbb66',
        600: '#80ffbb80',
        700: '#80ffbb99',
        800: '#80ffbbb2',
        900: '#80ffbbcc',
        950: '#80ffbbe5',
        1000: '#80ffbb',
      },
      orbitingSeagrassA: {
        0: '#9eff8000',
        50: '#9eff800a',
        100: '#9eff8014',
        200: '#9eff801f',
        300: '#9eff8033',
        400: '#9eff804d',
        500: '#9eff8066',
        600: '#9eff8080',
        700: '#9eff8099',
        800: '#9eff80b2',
        900: '#9eff80cc',
        950: '#9eff80e5',
        1000: '#9eff80',
      },
      nebulaSproutA: {
        0: '#80ff8200',
        50: '#80ff820a',
        100: '#80ff8214',
        200: '#80ff821f',
        300: '#80ff8233',
        400: '#80ff824d',
        500: '#80ff8266',
        600: '#80ff8280',
        700: '#80ff8299',
        800: '#80ff82b2',
        900: '#80ff82cc',
        950: '#80ff82e5',
        1000: '#80ff82',
      },
      abyssalHullA: {
        50: '#73817d0a',
        100: '#73817d14',
        200: '#73817d1f',
        300: '#73817d33',
        400: '#73817d4d',
        500: '#73817d66',
        600: '#73817d80',
        700: '#73817d99',
        800: '#73817db2',
        900: '#73817dcc',
        950: '#73817de5',
        1000: '#73817d',
      },
      tidalConcreteA: {
        50: '#4a55530a',
        100: '#4a555314',
        200: '#4a55531f',
        300: '#4a555333',
        400: '#4a55534d',
        500: '#4a555366',
        600: '#4a555380',
        700: '#4a555399',
        800: '#4a5553b2',
        900: '#4a5553cc',
        950: '#4a5553e5',
        1000: '#4a5553',
      },
      tidalAuroraA: {
        0: '#80ffc200',
        50: '#80ffc20a',
        100: '#80ffc214',
        200: '#80ffc21f',
        300: '#80ffc233',
        400: '#80ffc24d',
        500: '#80ffc266',
        600: '#80ffc280',
        700: '#80ffc299',
        800: '#80ffc2b2',
        900: '#80ffc2cc',
        950: '#80ffc2e5',
        1000: '#80ffc2',
      },
      greenlineCurrentA: {
        0: '#5fe46100',
        50: '#5fe4610a',
        100: '#5fe46114',
        200: '#5fe4611f',
        300: '#5fe46133',
        400: '#5fe4614d',
        500: '#5fe46166',
        600: '#5fe46180',
        700: '#5fe46199',
        800: '#5fe461b2',
        900: '#5fe461cc',
        950: '#5fe461e5',
        1000: '#5fe461',
      },
      lunarSeagrassA: {
        0: '#9df39f00',
        50: '#9df39f0a',
        100: '#9df39f14',
        200: '#9df39f1f',
        300: '#9df39f33',
        400: '#9df39f4d',
        500: '#9df39f66',
        600: '#9df39f80',
        700: '#9df39f99',
        800: '#9df39fb2',
        900: '#9df39fcc',
        950: '#9df39fe5',
        1000: '#9df39f',
      },
      zeroGMintA: {
        0: '#b8ffd900',
        50: '#b8ffd90a',
        100: '#b8ffd914',
        200: '#b8ffd91f',
        300: '#b8ffd933',
        400: '#b8ffd94d',
        500: '#b8ffd966',
        600: '#b8ffd980',
        700: '#b8ffd999',
        800: '#b8ffd9b2',
        900: '#b8ffd9cc',
        950: '#b8ffd9e5',
        1000: '#b8ffd9',
      },
      photosynthGreenA: {
        0: '#2a950000',
        50: '#2a95000a',
        100: '#2a950014',
        200: '#2a95001f',
        300: '#2a950033',
        400: '#2a95004d',
        500: '#2a950066',
        600: '#2a950080',
        700: '#2a950099',
        800: '#2a9500b2',
        900: '#2a9500cc',
        950: '#2a9500e5',
        1000: '#2a9500',
      },
      meadowOrbitA: {
        0: '#5fb30000',
        50: '#5fb3000a',
        100: '#5fb30014',
        200: '#5fb3001f',
        300: '#5fb30033',
        400: '#5fb3004d',
        500: '#5fb30066',
        600: '#5fb30080',
        700: '#5fb30099',
        800: '#5fb300b2',
        900: '#5fb300cc',
        950: '#5fb300e5',
        1000: '#5fb300',
      },
      verdantTrajectoryA: {
        0: '#00b30000',
        50: '#00b3000a',
        100: '#00b30014',
        200: '#00b3001f',
        300: '#00b30033',
        400: '#00b3004d',
        500: '#00b30066',
        600: '#00b30080',
        700: '#00b30099',
        800: '#00b300b2',
        900: '#00b300cc',
        950: '#00b300e5',
        1000: '#00b300',
      },
      lagoonVectorA: {
        0: '#00a67600',
        50: '#00a6760a',
        100: '#00a67614',
        200: '#00a6761f',
        300: '#00a67633',
        400: '#00a6764d',
        500: '#00a67666',
        600: '#00a67680',
        700: '#00a67699',
        800: '#00a676b2',
        900: '#00a676cc',
        950: '#00a676e5',
        1000: '#00a676',
      },
      kelpwayGreenA: {
        0: '#3aa03000',
        50: '#3aa0300a',
        100: '#3aa03014',
        200: '#3aa0301f',
        300: '#3aa03033',
        400: '#3aa0304d',
        500: '#3aa03066',
        600: '#3aa03080',
        700: '#3aa03099',
        800: '#3aa030b2',
        900: '#3aa030cc',
        950: '#3aa030e5',
        1000: '#3aa030',
      },
      leadlineVectorA: {
        0: '#4d9d4f00',
        50: '#4d9d4f0a',
        100: '#4d9d4f14',
        200: '#4d9d4f1f',
        300: '#4d9d4f33',
        400: '#4d9d4f4d',
        500: '#4d9d4f66',
        600: '#4d9d4f80',
        700: '#4d9d4f99',
        800: '#4d9d4fb2',
        900: '#4d9d4fcc',
        950: '#4d9d4fe5',
        1000: '#4d9d4f',
      },
      limegreenO: {
        50: '#e8f9d6',
        100: '#d9f3bd',
        200: '#caeca4',
        300: '#bbe589',
        400: '#acdf6b',
        500: '#9dd749',
        600: '#8ed000',
        700: '#72ac00',
        800: '#568a00',
        900: '#3c6900',
        950: '#244a00',
        1000: '#0d2c00',
      },
      abyssalHullO: {
        50: '#f8f9f9',
        100: '#f3f4f4',
        200: '#edefef',
        300: '#e2e5e4',
        400: '#d4d9d7',
        500: '#c6ccca',
        600: '#b9c0be',
        700: '#abb3b1',
        800: '#9da7a4',
        900: '#8f9a97',
        950: '#818e8a',
        1000: '#73817d',
        1100: '#5f6b68',
        1200: '#4c5553',
        1300: '#383f3d',
        1400: '#252928',
        1500: '#111313',
      },
      biolumeKelpO: {
        50: '#f6fdf4',
        100: '#eefbea',
        200: '#e6fae0',
        300: '#d7f7cb',
        400: '#c3f3b2',
        500: '#b0ef98',
        600: '#9cec7f',
        700: '#88e866',
        800: '#75e44c',
        900: '#61e033',
        950: '#4edd19',
        1000: '#3ad900',
        1100: '#32ba00',
        1200: '#2a9c00',
        1300: '#217d00',
        1400: '#195e00',
        1500: '#113f00',
        1600: '#092100',
      },
      emeraldSaberO: {
        50: '#f4fcf8',
        100: '#eaf9f3',
        200: '#e0f7ed',
        300: '#cbf2e1',
        400: '#b2edd3',
        500: '#98e7c5',
        600: '#7fe1b7',
        700: '#66dba8',
        800: '#4cd59a',
        900: '#33d08c',
        950: '#19ca7d',
        1000: '#00c46f',
        1100: '#00a35c',
        1200: '#008149',
        1300: '#006036',
        1400: '#003f24',
        1500: '#001d11',
      },
      forestO: {
        50: '#f6f8f4',
        100: '#eef2eb',
        200: '#e6ece1',
        300: '#d5e0ce',
        400: '#c1d0b5',
        500: '#adc19d',
        600: '#99b285',
        700: '#84a36d',
        800: '#709455',
        900: '#5c843c',
        950: '#477524',
        1000: '#33660c',
        1100: '#285009',
        1200: '#1d3b07',
        1300: '#122504',
        1400: '#080f02',
      },
      greenlineCurrentO: {
        50: '#f8fdf8',
        100: '#f1fcf1',
        200: '#ebfbeb',
        300: '#def9df',
        400: '#cef6cf',
        500: '#bef4bf',
        600: '#aff1b0',
        700: '#9feea0',
        800: '#8fec90',
        900: '#7fe980',
        950: '#6fe771',
        1000: '#5fe461',
        1100: '#38dd3a',
        1200: '#21c624',
        1300: '#1b9f1d',
        1400: '#147816',
        1500: '#0e510f',
        1600: '#072908',
      },
      ionSproutO: {
        50: '#fafcf4',
        100: '#f5faea',
        200: '#f1f8e0',
        300: '#e8f5cb',
        400: '#dcf0b2',
        500: '#d1ec98',
        600: '#c6e77f',
        700: '#bbe266',
        800: '#b0de4c',
        900: '#a4d933',
        950: '#99d519',
        1000: '#8ed000',
        1100: '#7ab300',
        1200: '#669500',
        1300: '#527800',
        1400: '#3e5a00',
        1500: '#293d00',
        1600: '#151f00',
      },
      kelpwayGreenO: {
        50: '#f6faf6',
        100: '#eef6ee',
        200: '#e6f3e5',
        300: '#d7ebd5',
        400: '#c3e2c0',
        500: '#b0d8ac',
        600: '#9ccf97',
        700: '#88c682',
        800: '#75bc6e',
        900: '#61b359',
        950: '#4ea945',
        1000: '#3aa030',
        1100: '#308528',
        1200: '#266a20',
        1300: '#1c4e18',
        1400: '#13330f',
        1500: '#091807',
      },
      lagoonVectorO: {
        50: '#f4faf9',
        100: '#eaf7f3',
        200: '#e0f3ee',
        300: '#cbece3',
        400: '#b2e4d5',
        500: '#98dbc8',
        600: '#7fd2ba',
        700: '#66c9ac',
        800: '#4cc09f',
        900: '#33b891',
        950: '#19af84',
        1000: '#00a676',
        1100: '#008a62',
        1200: '#006e4e',
        1300: '#00513a',
        1400: '#003526',
        1500: '#001912',
      },
      leadlineVectorO: {
        50: '#f7faf7',
        100: '#f0f6f0',
        200: '#e9f2e9',
        300: '#dbebdb',
        400: '#c9e1ca',
        500: '#b7d7b8',
        600: '#a6cea7',
        700: '#94c495',
        800: '#82ba84',
        900: '#70b072',
        950: '#5fa761',
        1000: '#4d9d4f',
        1100: '#408242',
        1200: '#336834',
        1300: '#264d27',
        1400: '#193219',
        1500: '#0c180c',
      },
      lunarSeagrassO: {
        50: '#fafefa',
        100: '#f6fdf6',
        200: '#f2fdf3',
        300: '#ebfceb',
        400: '#e1fbe2',
        500: '#d7fad8',
        600: '#cef9cf',
        700: '#c4f7c5',
        800: '#baf6bc',
        900: '#b0f5b2',
        950: '#a7f4a9',
        1000: '#9df39f',
        1100: '#6bed6e',
        1200: '#38e73c',
        1300: '#19cd1d',
        1400: '#139a16',
        1500: '#0d680f',
        1600: '#073508',
      },
      meadowOrbitO: {
        50: '#f8fbf4',
        100: '#f1f8ea',
        200: '#ebf5e0',
        300: '#deefcb',
        400: '#cee8b2',
        500: '#bee098',
        600: '#afd97f',
        700: '#9fd166',
        800: '#8fca4c',
        900: '#7fc233',
        950: '#6fbb19',
        1000: '#5fb300',
        1100: '#4f9500',
        1200: '#3f7600',
        1300: '#2f5800',
        1400: '#1e3900',
        1500: '#0e1b00',
      },
      natureO: {
        50: '#f6f8f4',
        100: '#eef2eb',
        200: '#e6ece1',
        300: '#d5e0ce',
        400: '#c1d0b5',
        500: '#adc19d',
        600: '#99b285',
        700: '#84a36d',
        800: '#709455',
        900: '#5c843c',
        950: '#477524',
        1000: '#33660c',
        1100: '#285009',
        1200: '#1d3b07',
        1300: '#122504',
        1400: '#080f02',
      },
      nebulaSproutO: {
        50: '#f9fef9',
        100: '#f4fef4',
        200: '#effeef',
        300: '#e5fee5',
        400: '#d8fed9',
        500: '#ccfecc',
        600: '#bfffc0',
        700: '#b2ffb4',
        800: '#a6ffa7',
        900: '#99ff9b',
        950: '#8dff8e',
        1000: '#80ff82',
        1100: '#4aff4d',
        1200: '#13ff17',
        1300: '#00dc03',
        1400: '#00a603',
        1500: '#007002',
        1600: '#003901',
      },
      orbitingSeagrassO: {
        50: '#fafef9',
        100: '#f6fef4',
        200: '#f2feef',
        300: '#ebfee5',
        400: '#e1fed8',
        500: '#d8fecc',
        600: '#ceffbf',
        700: '#c4ffb2',
        800: '#bbffa6',
        900: '#b1ff99',
        950: '#a8ff8d',
        1000: '#9eff80',
        1100: '#75ff4a',
        1200: '#4bff13',
        1300: '#34dc00',
        1400: '#27a600',
        1500: '#1a7000',
        1600: '#0e3900',
      },
      photosynthGreenO: {
        50: '#f6faf4',
        100: '#edf6ea',
        200: '#e5f1e0',
        300: '#d4e9cb',
        400: '#bedfb2',
        500: '#a9d498',
        600: '#94ca7f',
        700: '#7fbf66',
        800: '#6ab54c',
        900: '#54aa33',
        950: '#3fa019',
        1000: '#2a9500',
        1100: '#237c00',
        1200: '#1c6200',
        1300: '#154900',
        1400: '#0d3000',
        1500: '#061600',
      },
      planktonDriftO: {
        50: '#f9fefb',
        100: '#f4fef9',
        200: '#effef6',
        300: '#e5fef1',
        400: '#d8feea',
        500: '#ccfee3',
        600: '#bfffdd',
        700: '#b2ffd6',
        800: '#a6ffcf',
        900: '#99ffc8',
        950: '#8dffc2',
        1000: '#80ffbb',
        1100: '#4aff9e',
        1200: '#13ff81',
        1300: '#00dc66',
        1400: '#00a64d',
        1500: '#007034',
        1600: '#00391b',
      },
      tidalAuroraO: {
        50: '#f9fefc',
        100: '#f4fef9',
        200: '#effef7',
        300: '#e5fef2',
        400: '#d8feec',
        500: '#ccfee6',
        600: '#bfffe0',
        700: '#b2ffda',
        800: '#a6ffd4',
        900: '#99ffce',
        950: '#8dffc8',
        1000: '#80ffc2',
        1100: '#4affa8',
        1200: '#13ff8e',
        1300: '#00dc72',
        1400: '#00a656',
        1500: '#00703a',
        1600: '#00391e',
      },
      tidalConcreteO: {
        50: '#f7f7f7',
        100: '#f0f0f0',
        200: '#e8eae9',
        300: '#dadcdc',
        400: '#c8cbcb',
        500: '#b6baba',
        600: '#a4aaa9',
        700: '#929997',
        800: '#808886',
        900: '#6e7775',
        950: '#5c6664',
        1000: '#4a5553',
        1100: '#3a4341',
        1200: '#2b3130',
        1300: '#1b1f1e',
        1400: '#0b0d0c',
      },
      verdantFluxO: {
        50: '#f7fbf5',
        100: '#f1f9ed',
        200: '#eaf6e4',
        300: '#dcf1d2',
        400: '#cbebbd',
        500: '#bbe4a7',
        600: '#aade91',
        700: '#99d87b',
        800: '#88d165',
        900: '#77cb50',
        950: '#66c43a',
        1000: '#55be24',
        1100: '#49a31f',
        1200: '#3d881a',
        1300: '#316d15',
        1400: '#255210',
        1500: '#19370a',
        1600: '#0d1d05',
      },
      verdantTrajectoryO: {
        50: '#f4fbf4',
        100: '#eaf8ea',
        200: '#e0f5e0',
        300: '#cbefcb',
        400: '#b2e8b2',
        500: '#98e098',
        600: '#7fd97f',
        700: '#66d166',
        800: '#4cca4c',
        900: '#33c233',
        950: '#19bb19',
        1000: '#00b300',
        1100: '#009500',
        1200: '#007600',
        1300: '#005800',
        1400: '#003900',
        1500: '#001b00',
      },
      zeroGMintO: {
        50: '#fbfefd',
        100: '#f8fefb',
        200: '#f6fefa',
        300: '#f0fef7',
        400: '#e9fef3',
        500: '#e2feef',
        600: '#dbffec',
        700: '#d4ffe8',
        800: '#cdffe4',
        900: '#c6ffe0',
        950: '#bfffdd',
        1000: '#b8ffd9',
        1100: '#7affb8',
        1200: '#3cff96',
        1300: '#00fc75',
        1400: '#00be58',
        1500: '#00803c',
        1600: '#00421f',
      },
    },
    red: {
      infernalBladeA: {
        50: '#fc12340a',
        100: '#fc123414',
        200: '#fc12341f',
        300: '#fc123433',
        400: '#fc12344d',
        500: '#fc123466',
        600: '#fc123480',
        700: '#fc123499',
        800: '#fc1234b2',
        900: '#fc1234cc',
        950: '#fc1234e5',
        1000: '#fc1234',
      },
      malachor: {
        50: '#f7f2f2',
        100: '#f0e4e4',
        200: '#dfc6c6',
        300: '#cda1a1',
        400: '#ba7272',
        500: '#a40808',
        600: '#930707',
        700: '#7f0606',
        800: '#680505',
        900: '#490404',
        950: '#240202',
        1000: '#0a0101',
      },
      malachorA: {
        50: '#a408080a',
        100: '#a4080814',
        200: '#a408081f',
        300: '#a4080833',
        400: '#a408084d',
        500: '#a4080866',
        600: '#a4080880',
        700: '#a4080899',
        800: '#a40808b2',
        900: '#a40808cc',
        950: '#a40808e5',
        1000: '#a40808',
      },
      crimsonWrath: {
        50: '#fce5e6',
        100: '#ff7577',
        200: '#e85959',
        300: '#d64242',
        400: '#c82727',
        500: '#af0003',
        600: '#850003',
        700: '#490001',
        800: '#390001',
        900: '#200204',
        950: '#140103',
        1000: '#080001',
      },
      crimsonWrathA: {
        50: '#e000070a',
        100: '#e0000714',
        200: '#e000071f',
        300: '#e0000733',
        400: '#e000074d',
        500: '#e0000766',
        600: '#e0000780',
        700: '#e0000799',
        800: '#e00007b2',
        900: '#e00007cc',
        950: '#e00007e5',
        1000: '#e00007',
      },
      coralCometA: {
        50: '#eb939a0a',
        100: '#eb939a14',
        200: '#eb939a1f',
        300: '#eb939a33',
        400: '#eb939a4d',
        500: '#eb939a66',
        600: '#eb939a80',
        700: '#eb939a99',
        800: '#eb939ab2',
        900: '#eb939acc',
        950: '#eb939ae5',
        1000: '#eb939a',
      },
      redshiftAlertA: {
        50: '#ff1e000a',
        100: '#ff1e0014',
        200: '#ff1e001f',
        300: '#ff1e0033',
        400: '#ff1e004d',
        500: '#ff1e0066',
        600: '#ff1e0080',
        700: '#ff1e0099',
        800: '#ff1e00b2',
        900: '#ff1e00cc',
        950: '#ff1e00e5',
        1000: '#ff1e00',
      },
      solarBloomRedA: {
        50: '#c41e540a',
        100: '#c41e5414',
        200: '#c41e541f',
        300: '#c41e5433',
        400: '#c41e544d',
        500: '#c41e5466',
        600: '#c41e5480',
        700: '#c41e5499',
        800: '#c41e54b2',
        900: '#c41e54cc',
        950: '#c41e54e5',
        1000: '#c41e54',
      },
      solarFlareWarningA: {
        50: '#800f000a',
        100: '#800f0014',
        200: '#800f001f',
        300: '#800f0033',
        400: '#800f004d',
        500: '#800f0066',
        600: '#800f0080',
        700: '#800f0099',
        800: '#800f00b2',
        900: '#800f00cc',
        950: '#800f00e5',
        1000: '#800f00',
      },
      coralOrbitA: {
        50: '#b54d5a0a',
        100: '#b54d5a14',
        200: '#b54d5a1f',
        300: '#b54d5a33',
        400: '#b54d5a4d',
        500: '#b54d5a66',
        600: '#b54d5a80',
        700: '#b54d5a99',
        800: '#b54d5ab2',
        900: '#b54d5acc',
        950: '#b54d5ae5',
        1000: '#b54d5a',
      },
      criticalReentryA: {
        50: '#cc16000a',
        100: '#cc160014',
        200: '#cc16001f',
        300: '#cc160033',
        400: '#cc16004d',
        500: '#cc160066',
        600: '#cc160080',
        700: '#cc160099',
        800: '#cc1600b2',
        900: '#cc1600cc',
        950: '#cc1600e5',
        1000: '#cc1600',
      },
      crimsonO: {
        50: '#ffe4e0',
        100: '#fdccc7',
        200: '#f8b0ac',
        300: '#f29490',
        400: '#e87674',
        500: '#db5859',
        600: '#cc373c',
        700: '#b02a31',
        800: '#952028',
        900: '#7a1821',
        950: '#5f121b',
        1000: '#460d16',
      },
      coralCometO: {
        50: '#fdfafa',
        100: '#fcf5f6',
        200: '#fcf1f2',
        300: '#fae9ea',
        400: '#f8dee0',
        500: '#f6d3d6',
        600: '#f5c9cc',
        700: '#f3bec2',
        800: '#f1b3b8',
        900: '#efa8ae',
        950: '#ed9ea4',
        1000: '#eb939a',
        1100: '#e3656f',
        1200: '#da3845',
        1300: '#b9222e',
        1400: '#8c1a23',
        1500: '#5e1118',
        1600: '#30090c',
      },
      coralOrbitO: {
        50: '#fbf7f7',
        100: '#f8f0f1',
        200: '#f5e9ea',
        300: '#efdbdd',
        400: '#e8c9cd',
        500: '#e1b7bc',
        600: '#daa6ac',
        700: '#d2949c',
        800: '#cb828b',
        900: '#c4707b',
        950: '#bc5f6a',
        1000: '#b54d5a',
        1100: '#973f4a',
        1200: '#78323b',
        1300: '#59252c',
        1400: '#3a181d',
        1500: '#1b0b0d',
      },
      crimsonWrathO: {
        50: '#fdf4f4',
        100: '#fceaea',
        200: '#fae0e0',
        300: '#f8cbcd',
        400: '#f5b2b4',
        500: '#f2989b',
        600: '#ef7f83',
        700: '#ec666a',
        800: '#e94c51',
        900: '#e63339',
        950: '#e31920',
        1000: '#e00007',
        1100: '#a10005',
        1200: '#610003',
        1300: '#220001',
      },
      criticalReentryO: {
        50: '#fcf5f4',
        100: '#faebea',
        200: '#f8e2e0',
        300: '#f4d0cb',
        400: '#efb8b2',
        500: '#eaa198',
        600: '#e58a7f',
        700: '#e07366',
        800: '#db5c4c',
        900: '#d64433',
        950: '#d12d19',
        1000: '#cc1600',
        1100: '#a11100',
        1200: '#750d00',
        1300: '#4a0800',
        1400: '#1f0300',
      },
      infernalBladeO: {
        50: '#fef5f6',
        100: '#feebee',
        200: '#fee2e6',
        300: '#fecfd6',
        400: '#fdb7c1',
        500: '#fda0ad',
        600: '#fd8899',
        700: '#fd7085',
        800: '#fd5971',
        900: '#fc415c',
        950: '#fc2a48',
        1000: '#fc1234',
        1100: '#d20321',
        1200: '#990218',
        1300: '#61010f',
        1400: '#280106',
      },
      malachorO: {
        50: '#faf4f4',
        100: '#f7eaea',
        200: '#f3e0e0',
        300: '#eccdcd',
        400: '#e3b4b4',
        500: '#da9c9c',
        600: '#d18383',
        700: '#c86a6a',
        800: '#bf5252',
        900: '#b63939',
        950: '#ad2121',
        1000: '#a40808',
        1100: '#760606',
        1200: '#470303',
        1300: '#190101',
      },
      redshiftAlertO: {
        50: '#fef5f4',
        100: '#feecea',
        200: '#fee3e0',
        300: '#fed1cb',
        400: '#febbb2',
        500: '#fea498',
        600: '#ff8e7f',
        700: '#ff7866',
        800: '#ff614c',
        900: '#ff4b33',
        950: '#ff3419',
        1000: '#ff1e00',
        1100: '#c91800',
        1200: '#931100',
        1300: '#5c0b00',
        1400: '#260500',
      },
      solarBloomRedO: {
        50: '#fcf5f7',
        100: '#f9ecf0',
        200: '#f7e3ea',
        300: '#f2d1dc',
        400: '#edbbcb',
        500: '#e7a4ba',
        600: '#e18ea9',
        700: '#db7898',
        800: '#d56187',
        900: '#d04b76',
        950: '#ca3465',
        1000: '#c41e54',
        1100: '#9a1842',
        1200: '#711130',
        1300: '#470b1e',
        1400: '#1d050d',
      },
      solarFlareWarningO: {
        50: '#f9f4f4',
        100: '#f4ebea',
        200: '#efe1e0',
        300: '#e5cecb',
        400: '#d8b6b2',
        500: '#cc9e98',
        600: '#bf877f',
        700: '#b26f66',
        800: '#a6574c',
        900: '#993f33',
        950: '#8d2719',
        1000: '#800f00',
        1100: '#5c0b00',
        1200: '#370700',
        1300: '#130200',
      },
    },
    yellow: {
      twinSunlightA: {
        50: '#ffc6000a',
        100: '#ffc60014',
        200: '#ffc6001f',
        300: '#ffc60033',
        400: '#ffc6004d',
        500: '#ffc60066',
        600: '#ffc60080',
        700: '#ffc60099',
        800: '#ffc600b2',
        900: '#ffc600cc',
        950: '#ffc600e5',
        1000: '#ffc600',
      },
      twinSunlight: {
        50: '#fff9e5',
        100: '#fff1bf',
        200: '#ffcb66',
        300: '#ffc147',
        400: '#ffb41e',
        500: '#ffa500',
        600: '#e98702',
        700: '#b35d00',
        800: '#804301',
        900: '#663501',
        950: '#331b00',
        1000: '#1a0d00',
      },
      stellarAmber: {
        50: '#fdf9e9',
        100: '#fbf2d0',
        200: '#faecb5',
        300: '#f8e594',
        400: '#f6de69',
        500: '#f4d70d',
        600: '#dfc40c',
        700: '#c7af0b',
        800: '#ad9809',
        900: '#8d7c08',
        950: '#635805',
        1000: '#4d4404',
      },
      stellarAmberA: {
        50: '#dfc40c0a',
        100: '#dfc40c14',
        200: '#dfc40c1f',
        300: '#dfc40c33',
        400: '#dfc40c4d',
        500: '#dfc40c66',
        600: '#dfc40c80',
        700: '#dfc40c99',
        800: '#dfc40cb2',
        900: '#dfc40ccc',
        950: '#dfc40ce5',
        1000: '#dfc40c',
      },
      sunnyA: {
        0: '#ffb41e00',
        50: '#ffb41e0a',
        100: '#ffb41e14',
        200: '#ffb41e1f',
        300: '#ffb41e33',
        400: '#ffb41e4d',
        500: '#ffb41e66',
        600: '#ffb41e80',
        700: '#ffb41e99',
        800: '#ffb41eb2',
        900: '#ffb41ecc',
        950: '#ffb41ee5',
        1000: '#ffb41e',
      },
      solarKelpGlowA: {
        0: '#ffdd0000',
        50: '#ffdd000a',
        100: '#ffdd0014',
        200: '#ffdd001f',
        300: '#ffdd0033',
        400: '#ffdd004d',
        500: '#ffdd0066',
        600: '#ffdd0080',
        700: '#ffdd0099',
        800: '#ffdd00b2',
        900: '#ffdd00cc',
        950: '#ffdd00e5',
        1000: '#ffdd00',
      },
      starlitSandbarA: {
        0: '#ffee8000',
        50: '#ffee800a',
        100: '#ffee8014',
        200: '#ffee801f',
        300: '#ffee8033',
        400: '#ffee804d',
        500: '#ffee8066',
        600: '#ffee8080',
        700: '#ffee8099',
        800: '#ffee80b2',
        900: '#ffee80cc',
        950: '#ffee80e5',
        1000: '#ffee80',
      },
      radiantShoalA: {
        0: '#edef7d00',
        50: '#edef7d0a',
        100: '#edef7d14',
        200: '#edef7d1f',
        300: '#edef7d33',
        400: '#edef7d4d',
        500: '#edef7d66',
        600: '#edef7d80',
        700: '#edef7d99',
        800: '#edef7db2',
        900: '#edef7dcc',
        950: '#edef7de5',
        1000: '#edef7d',
      },
      saturnRingGoldA: {
        0: '#f6f09000',
        50: '#f6f0900a',
        100: '#f6f09014',
        200: '#f6f0901f',
        300: '#f6f09033',
        400: '#f6f0904d',
        500: '#f6f09066',
        600: '#f6f09080',
        700: '#f6f09099',
        800: '#f6f090b2',
        900: '#f6f090cc',
        950: '#f6f090e5',
        1000: '#f6f090',
      },
      photosphereGlowA: {
        0: '#edf08000',
        50: '#edf0800a',
        100: '#edf08014',
        200: '#edf0801f',
        300: '#edf08033',
        400: '#edf0804d',
        500: '#edf08066',
        600: '#edf08080',
        700: '#edf08099',
        800: '#edf080b2',
        900: '#edf080cc',
        950: '#edf080e5',
        1000: '#edf080',
      },
      stellarKelpGoldA: {
        0: '#b8a60000',
        50: '#b8a6000a',
        100: '#b8a60014',
        200: '#b8a6001f',
        300: '#b8a60033',
        400: '#b8a6004d',
        500: '#b8a60066',
        600: '#b8a60080',
        700: '#b8a60099',
        800: '#b8a600b2',
        900: '#b8a600cc',
        950: '#b8a600e5',
        1000: '#b8a600',
      },
      sunlitSandbankA: {
        0: '#a8950000',
        50: '#a895000a',
        100: '#a8950014',
        200: '#a895001f',
        300: '#a8950033',
        400: '#a895004d',
        500: '#a8950066',
        600: '#a8950080',
        700: '#a8950099',
        800: '#a89500b2',
        900: '#a89500cc',
        950: '#a89500e5',
        1000: '#a89500',
      },
      goldenShoalA: {
        0: '#b39b0000',
        50: '#b39b000a',
        100: '#b39b0014',
        200: '#b39b001f',
        300: '#b39b0033',
        400: '#b39b004d',
        500: '#b39b0066',
        600: '#b39b0080',
        700: '#b39b0099',
        800: '#b39b00b2',
        900: '#b39b00cc',
        950: '#b39b00e5',
        1000: '#b39b00',
      },
      solarAccretionA: {
        0: '#b3a00000',
        50: '#b3a0000a',
        100: '#b3a00014',
        200: '#b3a0001f',
        300: '#b3a00033',
        400: '#b3a0004d',
        500: '#b3a00066',
        600: '#b3a00080',
        700: '#b3a00099',
        800: '#b3a000b2',
        900: '#b3a000cc',
        950: '#b3a000e5',
        1000: '#b3a000',
      },
      eggshellO: {
        50: '#fff0d7',
        100: '#ffe7bf',
        200: '#ffdda6',
        300: '#ffd38d',
        400: '#ffc971',
        500: '#ffbf51',
        600: '#ffb41e',
        700: '#d59200',
        800: '#ad7200',
        900: '#875300',
        950: '#623600',
        1000: '#401a00',
        1100: '#1f0200',
      },
      goldenShoalO: {
        50: '#fbfaf4',
        100: '#f8f6ea',
        200: '#f5f2e0',
        300: '#efeacb',
        400: '#e8e0b2',
        500: '#e0d698',
        600: '#d9cd7f',
        700: '#d1c366',
        800: '#cab94c',
        900: '#c2af33',
        950: '#bba519',
        1000: '#b39b00',
        1100: '#9a8500',
        1200: '#806f00',
        1300: '#675900',
        1400: '#4e4300',
        1500: '#342d00',
        1600: '#1b1700',
      },
      photosphereGlowO: {
        50: '#fdfdf9',
        100: '#fdfdf4',
        200: '#fcfcef',
        300: '#fbfbe5',
        400: '#f9fad8',
        500: '#f7f8cc',
        600: '#f6f7bf',
        700: '#f4f6b2',
        800: '#f2f4a6',
        900: '#f0f399',
        950: '#eff18d',
        1000: '#edf080',
        1100: '#e6ea51',
        1200: '#e0e523',
        1300: '#b9bd16',
        1400: '#8b8f11',
        1500: '#5e600b',
        1600: '#303106',
      },
      radiantShoalO: {
        50: '#fdfdf9',
        100: '#fdfdf4',
        200: '#fcfcef',
        300: '#fbfbe4',
        400: '#f9fad7',
        500: '#f7f8ca',
        600: '#f6f7be',
        700: '#f4f5b1',
        800: '#f2f4a4',
        900: '#f0f297',
        950: '#eff18a',
        1000: '#edef7d',
        1100: '#e7e94f',
        1200: '#e0e421',
        1300: '#b7ba17',
        1400: '#8a8c11',
        1500: '#5d5f0c',
        1600: '#303106',
      },
      saturnRingGoldO: {
        50: '#fefdfa',
        100: '#fdfdf5',
        200: '#fdfcf1',
        300: '#fcfbe8',
        400: '#fcfadd',
        500: '#fbf8d2',
        600: '#faf7c7',
        700: '#f9f6bc',
        800: '#f8f4b1',
        900: '#f8f3a6',
        950: '#f7f19b',
        1000: '#f6f090',
        1100: '#f2e95d',
        1200: '#eee22a',
        1300: '#cfc411',
        1400: '#9c940d',
        1500: '#696409',
        1600: '#363304',
      },
      solarAccretionO: {
        50: '#fbfaf4',
        100: '#f8f6ea',
        200: '#f5f3e0',
        300: '#efebcb',
        400: '#e8e2b2',
        500: '#e0d898',
        600: '#d9cf7f',
        700: '#d1c666',
        800: '#cabc4c',
        900: '#c2b333',
        950: '#bba919',
        1000: '#b3a000',
        1100: '#9a8900',
        1200: '#807300',
        1300: '#675c00',
        1400: '#4e4500',
        1500: '#342f00',
        1600: '#1b1800',
      },
      solarKelpGlowO: {
        50: '#fefdf4',
        100: '#fefbea',
        200: '#fefae0',
        300: '#fef7cb',
        400: '#fef4b2',
        500: '#fef198',
        600: '#ffee7f',
        700: '#ffea66',
        800: '#ffe74c',
        900: '#ffe433',
        950: '#ffe019',
        1000: '#ffdd00',
        1100: '#dbbe00',
        1200: '#b79e00',
        1300: '#937f00',
        1400: '#6f6000',
        1500: '#4a4000',
        1600: '#262100',
      },
      starlitSandbarO: {
        50: '#fefdf9',
        100: '#fefdf4',
        200: '#fefcef',
        300: '#fefbe5',
        400: '#fef9d8',
        500: '#fef8cc',
        600: '#fff6bf',
        700: '#fff4b2',
        800: '#fff3a6',
        900: '#fff199',
        950: '#fff08d',
        1000: '#ffee80',
        1100: '#ffe74a',
        1200: '#ffdf13',
        1300: '#dcbf00',
        1400: '#a69000',
        1500: '#706100',
        1600: '#393200',
      },
      stellarAmberO: {
        50: '#fdfcf4',
        100: '#fcf9eb',
        200: '#faf7e1',
        300: '#f8f2ce',
        400: '#f5edb5',
        500: '#f2e79d',
        600: '#efe185',
        700: '#ebdb6d',
        800: '#e8d555',
        900: '#e5d03c',
        950: '#e2ca24',
        1000: '#dfc40c',
        1100: '#bfa80a',
        1200: '#a08c09',
        1300: '#807107',
        1400: '#615505',
        1500: '#413904',
        1600: '#211d02',
      },
      stellarKelpGoldO: {
        50: '#fbfaf4',
        100: '#f8f7ea',
        200: '#f6f3e0',
        300: '#f0eccb',
        400: '#e9e4b2',
        500: '#e2db98',
        600: '#dbd27f',
        700: '#d4c966',
        800: '#cdc04c',
        900: '#c6b833',
        950: '#bfaf19',
        1000: '#b8a600',
        1100: '#9e8e00',
        1200: '#847700',
        1300: '#6a5f00',
        1400: '#504800',
        1500: '#363000',
        1600: '#1c1900',
      },
      sunlitSandbankO: {
        50: '#fbfaf4',
        100: '#f7f6ea',
        200: '#f4f1e0',
        300: '#ede9cb',
        400: '#e4dfb2',
        500: '#dcd498',
        600: '#d3ca7f',
        700: '#cabf66',
        800: '#c2b54c',
        900: '#b9aa33',
        950: '#b1a019',
        1000: '#a89500',
        1100: '#8b7c00',
        1200: '#6f6200',
        1300: '#524900',
        1400: '#363000',
        1500: '#191600',
      },
      sunnyO: {
        50: '#fefbf5',
        100: '#fef8ec',
        200: '#fef5e3',
        300: '#feefd1',
        400: '#fee8bb',
        500: '#fee0a4',
        600: '#ffd98e',
        700: '#ffd278',
        800: '#ffca61',
        900: '#ffc34b',
        950: '#ffbb34',
        1000: '#ffb41e',
        1100: '#f5a300',
        1200: '#cc8800',
        1300: '#a46d00',
        1400: '#7c5200',
        1500: '#533700',
        1600: '#2b1d00',
      },
      twinSunlightO: {
        50: '#fefcf4',
        100: '#fefaea',
        200: '#fef7e0',
        300: '#fef3cb',
        400: '#feedb2',
        500: '#fee898',
        600: '#ffe27f',
        700: '#ffdc66',
        800: '#ffd74c',
        900: '#ffd133',
        950: '#ffcc19',
        1000: '#ffc600',
        1100: '#dbaa00',
        1200: '#b78e00',
        1300: '#937200',
        1400: '#6f5600',
        1500: '#4a3a00',
        1600: '#261e00',
      },
    },
    orange: {
      sunstreakA: {
        50: '#f685000a',
        100: '#f6850014',
        200: '#f685001f',
        300: '#f6850033',
        400: '#f685004d',
        500: '#f6850066',
        600: '#f6850080',
        700: '#f6850099',
        800: '#f68500b2',
        900: '#f68500cc',
        950: '#f68500e5',
        1000: '#f68500',
      },
      sunstreak: {
        50: '#fef3e5',
        100: '#ff987d',
        200: '#ff7c5a',
        300: '#ff673f',
        400: '#eb522a',
        500: '#d44621',
        600: '#9c3b21',
        700: '#713323',
        800: '#57291d',
        900: '#472218',
        950: '#2e1610',
        1000: '#140a07',
      },
      moltenFangA: {
        50: '#ee5e210a',
        100: '#ee5e2114',
        200: '#ee5e211f',
        300: '#ee5e2133',
        400: '#ee5e214d',
        500: '#ee5e2166',
        600: '#ee5e2180',
        700: '#ee5e2199',
        800: '#ee5e21b2',
        900: '#ee5e21cc',
        950: '#ee5e21e5',
        1000: '#ee5e21',
      },
      amberA: {
        0: '#9c3b2100',
        50: '#9c3b210a',
        100: '#9c3b2114',
        200: '#9c3b211f',
        300: '#9c3b2133',
        400: '#9c3b214d',
        500: '#9c3b2166',
        600: '#9c3b2180',
        700: '#9c3b2199',
        800: '#9c3b21b2',
        900: '#9c3b21cc',
        950: '#9c3b21e5',
        1000: '#9c3b21',
      },
      pumpkinA: {
        0: '#ff673f00',
        50: '#ff673f0a',
        100: '#ff673f14',
        200: '#ff673f1f',
        300: '#ff673f33',
        400: '#ff673f4d',
        500: '#ff673f66',
        600: '#ff673f80',
        700: '#ff673f99',
        800: '#ff673fb2',
        900: '#ff673fcc',
        950: '#ff673fe5',
        1000: '#ff673f',
      },
      warmA: {
        0: '#eb522a00',
        50: '#eb522a0a',
        100: '#eb522a14',
        200: '#eb522a1f',
        300: '#eb522a33',
        400: '#eb522a4d',
        500: '#eb522a66',
        600: '#eb522a80',
        700: '#eb522a99',
        800: '#eb522ab2',
        900: '#eb522acc',
        950: '#eb522ae5',
        1000: '#eb522a',
      },
      orbitAmberA: {
        0: '#ff9d0000',
        50: '#ff9d000a',
        100: '#ff9d0014',
        200: '#ff9d001f',
        300: '#ff9d0033',
        400: '#ff9d004d',
        500: '#ff9d0066',
        600: '#ff9d0080',
        700: '#ff9d0099',
        800: '#ff9d00b2',
        900: '#ff9d00cc',
        950: '#ff9d00e5',
        1000: '#ff9d00',
      },
      plasmaReefA: {
        0: '#ffb05400',
        50: '#ffb0540a',
        100: '#ffb05414',
        200: '#ffb0541f',
        300: '#ffb05433',
        400: '#ffb0544d',
        500: '#ffb05466',
        600: '#ffb05480',
        700: '#ffb05499',
        800: '#ffb054b2',
        900: '#ffb054cc',
        950: '#ffb054e5',
        1000: '#ffb054',
      },
      meteorDriftA: {
        0: '#ffb45400',
        50: '#ffb4540a',
        100: '#ffb45414',
        200: '#ffb4541f',
        300: '#ffb45433',
        400: '#ffb4544d',
        500: '#ffb45466',
        600: '#ffb45480',
        700: '#ffb45499',
        800: '#ffb454b2',
        900: '#ffb454cc',
        950: '#ffb454e5',
        1000: '#ffb454',
      },
      reentryFlameA: {
        0: '#f6aa1100',
        50: '#f6aa110a',
        100: '#f6aa1114',
        200: '#f6aa111f',
        300: '#f6aa1133',
        400: '#f6aa114d',
        500: '#f6aa1166',
        600: '#f6aa1180',
        700: '#f6aa1199',
        800: '#f6aa11b2',
        900: '#f6aa11cc',
        950: '#f6aa11e5',
        1000: '#f6aa11',
      },
      daybreakAmberA: {
        0: '#cc730000',
        50: '#cc73000a',
        100: '#cc730014',
        200: '#cc73001f',
        300: '#cc730033',
        400: '#cc73004d',
        500: '#cc730066',
        600: '#cc730080',
        700: '#cc730099',
        800: '#cc7300b2',
        900: '#cc7300cc',
        950: '#cc7300e5',
        1000: '#cc7300',
      },
      reentryBronzeA: {
        0: '#cc7a0000',
        50: '#cc7a000a',
        100: '#cc7a0014',
        200: '#cc7a001f',
        300: '#cc7a0033',
        400: '#cc7a004d',
        500: '#cc7a0066',
        600: '#cc7a0080',
        700: '#cc7a0099',
        800: '#cc7a00b2',
        900: '#cc7a00cc',
        950: '#cc7a00e5',
        1000: '#cc7a00',
      },
      signalAmberA: {
        0: '#cc7e0000',
        50: '#cc7e000a',
        100: '#cc7e0014',
        200: '#cc7e001f',
        300: '#cc7e0033',
        400: '#cc7e004d',
        500: '#cc7e0066',
        600: '#cc7e0080',
        700: '#cc7e0099',
        800: '#cc7e00b2',
        900: '#cc7e00cc',
        950: '#cc7e00e5',
        1000: '#cc7e00',
      },
      orbitalBurnA: {
        0: '#c6770000',
        50: '#c677000a',
        100: '#c6770014',
        200: '#c677001f',
        300: '#c6770033',
        400: '#c677004d',
        500: '#c6770066',
        600: '#c6770080',
        700: '#c6770099',
        800: '#c67700b2',
        900: '#c67700cc',
        950: '#c67700e5',
        1000: '#c67700',
      },
      burntSiennaO: {
        50: '#ffead6',
        100: '#ffdabb',
        200: '#ffcaa0',
        300: '#ffb983',
        400: '#fea866',
        500: '#fa9743',
        600: '#f68500',
        700: '#cf6a00',
        800: '#aa5000',
        900: '#863700',
        950: '#631f00',
        1000: '#420600',
        1100: '#230000',
      },
      amberO: {
        50: '#faf6f5',
        100: '#f6eeec',
        200: '#f2e7e3',
        300: '#ead7d2',
        400: '#e1c4bc',
        500: '#d7b0a6',
        600: '#cd9d90',
        700: '#c38979',
        800: '#b97663',
        900: '#b0624d',
        950: '#a64f37',
        1000: '#9c3b21',
        1100: '#7b2e1a',
        1200: '#5a2213',
        1300: '#39150c',
        1400: '#170905',
      },
      daybreakAmberO: {
        50: '#fcf8f4',
        100: '#faf3ea',
        200: '#f8ede0',
        300: '#f4e2cb',
        400: '#efd4b2',
        500: '#eac698',
        600: '#e5b97f',
        700: '#e0ab66',
        800: '#db9d4c',
        900: '#d68f33',
        950: '#d18119',
        1000: '#cc7300',
        1100: '#a95f00',
        1200: '#874c00',
        1300: '#643800',
        1400: '#412500',
        1500: '#1f1100',
      },
      meteorDriftO: {
        50: '#fefbf7',
        100: '#fef8f0',
        200: '#fef5ea',
        300: '#feefdc',
        400: '#fee8cb',
        500: '#fee0ba',
        600: '#ffd9a9',
        700: '#ffd298',
        800: '#ffca87',
        900: '#ffc376',
        950: '#ffbb65',
        1000: '#ffb454',
        1100: '#ff9f24',
        1200: '#f38800',
        1300: '#c36d00',
        1400: '#935200',
        1500: '#633800',
        1600: '#331d00',
      },
      moltenFangO: {
        50: '#fdf8f5',
        100: '#fdf1ec',
        200: '#fcebe3',
        300: '#fbded2',
        400: '#f9cebc',
        500: '#f8bea6',
        600: '#f6ae90',
        700: '#f49e79',
        800: '#f38e63',
        900: '#f17e4d',
        950: '#ef6e37',
        1000: '#ee5e21',
        1100: '#d14a10',
        1200: '#a63b0d',
        1300: '#7b2c0a',
        1400: '#501c06',
        1500: '#260d03',
      },
      orbitAmberO: {
        50: '#fefaf4',
        100: '#fef6ea',
        200: '#fef2e0',
        300: '#feebcb',
        400: '#fee1b2',
        500: '#fed798',
        600: '#ffce7f',
        700: '#ffc466',
        800: '#ffba4c',
        900: '#ffb033',
        950: '#ffa719',
        1000: '#ff9d00',
        1100: '#db8700',
        1200: '#b77100',
        1300: '#935a00',
        1400: '#6f4400',
        1500: '#4a2e00',
        1600: '#261800',
      },
      orbitalBurnO: {
        50: '#fcf9f4',
        100: '#faf3ea',
        200: '#f7eee0',
        300: '#f3e3cb',
        400: '#edd6b2',
        500: '#e8c898',
        600: '#e2bb7f',
        700: '#dcad66',
        800: '#d7a04c',
        900: '#d19233',
        950: '#cc8519',
        1000: '#c67700',
        1100: '#a46300',
        1200: '#834f00',
        1300: '#613a00',
        1400: '#3f2600',
        1500: '#1e1200',
      },
      plasmaReefO: {
        50: '#fefbf7',
        100: '#fef8f0',
        200: '#fef5ea',
        300: '#feeedc',
        400: '#fee7cb',
        500: '#fedfba',
        600: '#ffd7a9',
        700: '#ffcf98',
        800: '#ffc787',
        900: '#ffc076',
        950: '#ffb865',
        1000: '#ffb054',
        1100: '#ff9a24',
        1200: '#f38300',
        1300: '#c36900',
        1400: '#934f00',
        1500: '#633500',
        1600: '#331b00',
      },
      pumpkinO: {
        50: '#fef8f6',
        100: '#fef2ef',
        200: '#feece7',
        300: '#fee0d8',
        400: '#fed1c5',
        500: '#fec2b2',
        600: '#ffb39f',
        700: '#ffa38b',
        800: '#ff9478',
        900: '#ff8565',
        950: '#ff7652',
        1000: '#ff673f',
        1100: '#ff4312',
        1200: '#e42f00',
        1300: '#b72600',
        1400: '#8a1d00',
        1500: '#5d1300',
        1600: '#300a00',
      },
      reentryBronzeO: {
        50: '#fcf9f4',
        100: '#faf3ea',
        200: '#f8eee0',
        300: '#f4e4cb',
        400: '#efd6b2',
        500: '#eac998',
        600: '#e5bc7f',
        700: '#e0af66',
        800: '#dba24c',
        900: '#d69433',
        950: '#d18719',
        1000: '#cc7a00',
        1100: '#a96500',
        1200: '#875100',
        1300: '#643c00',
        1400: '#412700',
        1500: '#1f1200',
      },
      reentryFlameO: {
        50: '#fefbf5',
        100: '#fdf7eb',
        200: '#fdf4e2',
        300: '#fcedcf',
        400: '#fce5b7',
        500: '#fbdc9f',
        600: '#fad488',
        700: '#f9cc70',
        800: '#f8c358',
        900: '#f8bb40',
        950: '#f7b229',
        1000: '#f6aa11',
        1100: '#da9408',
        1200: '#b67c07',
        1300: '#926306',
        1400: '#6e4b04',
        1500: '#4a3203',
        1600: '#261a01',
      },
      signalAmberO: {
        50: '#fcf9f4',
        100: '#faf4ea',
        200: '#f8efe0',
        300: '#f4e4cb',
        400: '#efd8b2',
        500: '#eacb98',
        600: '#e5be7f',
        700: '#e0b166',
        800: '#dba44c',
        900: '#d69833',
        950: '#d18b19',
        1000: '#cc7e00',
        1100: '#a96900',
        1200: '#875300',
        1300: '#643e00',
        1400: '#412800',
        1500: '#1f1300',
      },
      sunstreakO: {
        50: '#fef9f4',
        100: '#fdf4ea',
        200: '#fdefe0',
        300: '#fce6cb',
        400: '#fcdab2',
        500: '#fbce98',
        600: '#fac27f',
        700: '#f9b566',
        800: '#f8a94c',
        900: '#f89d33',
        950: '#f79119',
        1000: '#f68500',
        1100: '#d37200',
        1200: '#b05f00',
        1300: '#8d4c00',
        1400: '#6b3a00',
        1500: '#482700',
        1600: '#251400',
      },
      warmO: {
        50: '#fdf7f6',
        100: '#fcf0ed',
        200: '#fce9e5',
        300: '#fadcd4',
        400: '#f8cabe',
        500: '#f6b9a9',
        600: '#f5a894',
        700: '#f3977f',
        800: '#f1866a',
        900: '#ef7454',
        950: '#ed633f',
        1000: '#eb522a',
        1100: '#d23b14',
        1200: '#a72f10',
        1300: '#7c230c',
        1400: '#511708',
        1500: '#260b04',
      },
    },
    purple: {
      voidshadeA: {
        50: '#601ef90a',
        100: '#601ef914',
        200: '#601ef91f',
        300: '#601ef933',
        400: '#601ef94d',
        500: '#601ef966',
        600: '#601ef980',
        700: '#601ef999',
        800: '#601ef9b2',
        900: '#601ef9cc',
        950: '#601ef9e5',
        1000: '#601ef9',
      },
      nexus: {
        50: '#f0e8fd',
        100: '#dac5f9',
        200: '#b58bf3',
        300: '#9152ee',
        400: '#6c18e8',
        500: '#5b14c5',
        600: '#4b10a1',
        700: '#3a0d7e',
        800: '#2a095b',
        900: '#190537',
        950: '#0e031f',
        1000: '#06010d',
      },
      nexusA: {
        50: '#6c18e80a',
        100: '#6c18e814',
        200: '#6c18e81f',
        300: '#6c18e833',
        400: '#6c18e84d',
        500: '#6c18e866',
        600: '#6c18e880',
        700: '#6c18e899',
        800: '#6c18e8b2',
        900: '#6c18e8cc',
        950: '#6c18e8e5',
        1000: '#6c18e8',
      },
      fuchsia: {
        50: '#f7f3fd',
        100: '#efecf9',
        200: '#d6cdee',
        300: '#bcafe4',
        400: '#a390da',
        500: '#8972d0',
        600: '#7053c5',
        700: '#6040bf',
        800: '#4b3295',
        900: '#3c2876',
        950: '#2c1d58',
        1000: '#1d1339',
      },
      fuchsiaA: {
        50: '#7053c50a',
        100: '#7053c514',
        200: '#7053c51f',
        300: '#7053c533',
        400: '#7053c54d',
        500: '#7053c566',
        600: '#7053c580',
        700: '#7053c599',
        800: '#7053c5b2',
        900: '#7053c5cc',
        950: '#7053c5e5',
        1000: '#7053c5',
      },
      violet: {
        50: '#f0e8fd',
        100: '#b8b1de',
        200: '#9f97ca',
        300: '#8178b3',
        400: '#7268a6',
        500: '#5a508a',
        600: '#473d77',
        700: '#3d3065',
        800: '#322558',
        900: '#190537',
        950: '#0e031f',
        1000: '#06010d',
      },
      violetA: {
        50: '#3225580a',
        100: '#32255814',
        200: '#3225581f',
        300: '#32255833',
        400: '#3225584d',
        500: '#32255866',
        600: '#32255880',
        700: '#32255899',
        800: '#322558b2',
        900: '#322558cc',
        950: '#322558e5',
        1000: '#322558',
      },
      deepPurpA: {
        0: '#5a508a00',
        50: '#5a508a0a',
        100: '#5a508a14',
        200: '#5a508a1f',
        300: '#5a508a33',
        400: '#5a508a4d',
        500: '#5a508a66',
        600: '#5a508a80',
        700: '#5a508a99',
        800: '#5a508ab2',
        900: '#5a508acc',
        950: '#5a508ae5',
        1000: '#5a508a',
      },
      nebulaLavenderA: {
        0: '#c1afff00',
        50: '#c1afff0a',
        100: '#c1afff14',
        200: '#c1afff1f',
        300: '#c1afff33',
        400: '#c1afff4d',
        500: '#c1afff66',
        600: '#c1afff80',
        700: '#c1afff99',
        800: '#c1afffb2',
        900: '#c1afffcc',
        950: '#c1afffe5',
        1000: '#c1afff',
      },
      ionBloomVioletA: {
        0: '#6d4dcc00',
        50: '#6d4dcc0a',
        100: '#6d4dcc14',
        200: '#6d4dcc1f',
        300: '#6d4dcc33',
        400: '#6d4dcc4d',
        500: '#6d4dcc66',
        600: '#6d4dcc80',
        700: '#6d4dcc99',
        800: '#6d4dccb2',
        900: '#6d4dcccc',
        950: '#6d4dcce5',
        1000: '#6d4dcc',
      },
      deepPurpO: {
        50: '#f7f7f9',
        100: '#f1f0f5',
        200: '#eae9f0',
        300: '#dddbe7',
        400: '#cdcadb',
        500: '#bcb8d0',
        600: '#aca7c4',
        700: '#9c96b8',
        800: '#8b84ad',
        900: '#7b73a1',
        950: '#6a6196',
        1000: '#5a508a',
        1100: '#473f6d',
        1200: '#342e4f',
        1300: '#211d32',
        1400: '#0e0c15',
      },
      fuchsiaO: {
        50: '#f8f7fc',
        100: '#f3f0f9',
        200: '#ede9f7',
        300: '#e2dcf3',
        400: '#d3cbed',
        500: '#c5bae7',
        600: '#b7a9e2',
        700: '#a997dc',
        800: '#9b86d6',
        900: '#8c75d0',
        950: '#7e64cb',
        1000: '#7053c5',
        1100: '#583bae',
        1200: '#462f8a',
        1300: '#342367',
        1400: '#221743',
        1500: '#100b1f',
      },
      ionBloomVioletO: {
        50: '#f8f7fc',
        100: '#f2f0fa',
        200: '#ede9f8',
        300: '#e1dbf4',
        400: '#d3c9ef',
        500: '#c4b7ea',
        600: '#b6a6e5',
        700: '#a794e0',
        800: '#9982db',
        900: '#8a70d6',
        950: '#7c5fd1',
        1000: '#6d4dcc',
        1100: '#5534b5',
        1200: '#432990',
        1300: '#321f6b',
        1400: '#211446',
        1500: '#0f0921',
      },
      nebulaLavenderO: {
        50: '#fcfbfe',
        100: '#f9f8fe',
        200: '#f7f5fe',
        300: '#f2eefe',
        400: '#ece6fe',
        500: '#e6defe',
        600: '#e0d7ff',
        700: '#d9cfff',
        800: '#d3c7ff',
        900: '#cdbfff',
        950: '#c7b7ff',
        1000: '#c1afff',
        1100: '#9272ff',
        1200: '#6335ff',
        1300: '#3800f7',
        1400: '#2a00ba',
        1500: '#1c007d',
        1600: '#0f0041',
      },
      nexusO: {
        50: '#f8f5fd',
        100: '#f2ecfc',
        200: '#ece2fb',
        300: '#e1d0fa',
        400: '#d2b9f7',
        500: '#c4a2f5',
        600: '#b58bf3',
        700: '#a674f1',
        800: '#985def',
        900: '#8946ec',
        950: '#7b2fea',
        1000: '#6c18e8',
        1100: '#5512b7',
        1200: '#3e0d86',
        1300: '#270854',
        1400: '#100323',
      },
      violetO: {
        50: '#f6f5f7',
        100: '#eeedf1',
        200: '#e6e4ea',
        300: '#d5d3dd',
        400: '#c1bdcc',
        500: '#aca7bc',
        600: '#9892ab',
        700: '#847c9a',
        800: '#6f668a',
        900: '#5b5079',
        950: '#463b69',
        1000: '#322558',
        1100: '#241b3f',
        1200: '#161026',
        1300: '#08060d',
      },
      voidshadeO: {
        50: '#f8f5fe',
        100: '#f1ecfe',
        200: '#ebe3fd',
        300: '#ded1fd',
        400: '#cfbbfd',
        500: '#bfa4fc',
        600: '#af8efc',
        700: '#9f78fb',
        800: '#8f61fb',
        900: '#804bfa',
        950: '#7034fa',
        1000: '#601ef9',
        1100: '#4406d6',
        1200: '#32049c',
        1300: '#200363',
        1400: '#0d0129',
      },
    },
    pink: {
      plasmaCircuit: {
        50: '#fde5f1',
        100: '#f3a7ba',
        200: '#f48ba6',
        300: '#eb7795',
        400: '#db6684',
        500: '#c55773',
        600: '#ac4a63',
        700: '#87374b',
        800: '#662737',
        900: '#551928',
        950: '#4d0e1e',
        1000: '#400c19',
      },
      plasmaCircuitA: {
        50: '#de006b0a',
        100: '#de006b14',
        200: '#de006b1f',
        300: '#de006b33',
        400: '#de006b4d',
        500: '#de006b66',
        600: '#de006b80',
        700: '#de006b99',
        800: '#de006bb2',
        900: '#de006bcc',
        950: '#de006be5',
        1000: '#de006b',
      },
      supernovaCoralA: {
        50: '#ff628c0a',
        100: '#ff628c14',
        200: '#ff628c1f',
        300: '#ff628c33',
        400: '#ff628c4d',
        500: '#ff628c66',
        600: '#ff628c80',
        700: '#ff628c99',
        800: '#ff628cb2',
        900: '#ff628ccc',
        950: '#ff628ce5',
        1000: '#ff628c',
      },
      mewMode: {
        50: '#e709730a',
        100: '#e7097314',
        200: '#e709731f',
        300: '#e7097333',
        400: '#e709734d',
        500: '#e7097366',
        600: '#e7097380',
        700: '#e7097399',
        800: '#e70973b2',
        900: '#e70973cc',
        950: '#e70973e5',
        1000: '#e70973',
      },
      cosmicAnemoneA: {
        50: '#ff80e10a',
        100: '#ff80e114',
        200: '#ff80e11f',
        300: '#ff80e133',
        400: '#ff80e14d',
        500: '#ff80e166',
        600: '#ff80e180',
        700: '#ff80e199',
        800: '#ff80e1b2',
        900: '#ff80e1cc',
        950: '#ff80e1e5',
        1000: '#ff80e1',
      },
      cosmicAnemoneO: {
        50: '#fef9fd',
        100: '#fef4fc',
        200: '#feeffb',
        300: '#fee5f8',
        400: '#fed8f5',
        500: '#feccf2',
        600: '#ffbff0',
        700: '#ffb2ed',
        800: '#ffa6ea',
        900: '#ff99e7',
        950: '#ff8de4',
        1000: '#ff80e1',
        1100: '#ff4ad4',
        1200: '#ff13c7',
        1300: '#dc00a8',
        1400: '#a6007f',
        1500: '#700055',
        1600: '#39002c',
      },
      plasmaCircuitO: {
        50: '#fdf4f8',
        100: '#fbeaf2',
        200: '#fae0ec',
        300: '#f8cbe1',
        400: '#f4b2d2',
        500: '#f198c3',
        600: '#ee7fb5',
        700: '#eb66a6',
        800: '#e84c97',
        900: '#e43388',
        950: '#e1197a',
        1000: '#de006b',
        1100: '#af0054',
        1200: '#80003e',
        1300: '#500027',
        1400: '#210010',
      },
      supernovaCoralO: {
        50: '#fef8f9',
        100: '#fef2f5',
        200: '#feebf0',
        300: '#fedfe7',
        400: '#fecfdc',
        500: '#fec0d0',
        600: '#ffb0c5',
        700: '#ffa0ba',
        800: '#ff91ae',
        900: '#ff81a3',
        950: '#ff7297',
        1000: '#ff628c',
        1100: '#ff3067',
        1200: '#fd0044',
        1300: '#cb0036',
        1400: '#990029',
        1500: '#67001c',
        1600: '#35000e',
      },
    },
    magenta: {
      magenta: {
        50: '#fde5f1',
        100: '#f652b1',
        200: '#d93896',
        300: '#b81c77',
        400: '#9e1364',
        500: '#82074f',
        600: '#6d0341',
        700: '#590134',
        800: '#49002b',
        900: '#380021',
        950: '#1f0012',
        1000: '#0d0008',
      },
      magentaA: {
        0: '#b81c770a',
        50: '#b81c770a',
        100: '#b81c7714',
        200: '#b81c771f',
        300: '#b81c7733',
        400: '#b81c774d',
        500: '#b81c7766',
        600: '#b81c7780',
        700: '#b81c7799',
        800: '#b81c77b2',
        900: '#b81c77cc',
        950: '#b81c77e5',
        1000: '#b81c77',
      },
      radiantAnemoneA: {
        0: '#cc00990a',
        50: '#cc00990a',
        100: '#cc009914',
        200: '#cc00991f',
        300: '#cc009933',
        400: '#cc00994d',
        500: '#cc009966',
        600: '#cc009980',
        700: '#cc009999',
        800: '#cc0099b2',
        900: '#cc0099cc',
        950: '#cc0099e5',
        1000: '#cc0099',
      },
      magentaO: {
        50: '#fbf5f9',
        100: '#f8ecf3',
        200: '#f6e3ee',
        300: '#f0d1e3',
        400: '#e9bad6',
        500: '#e2a4c8',
        600: '#db8dbb',
        700: '#d476ad',
        800: '#cd60a0',
        900: '#c64992',
        950: '#bf3385',
        1000: '#b81c77',
        1100: '#91165e',
        1200: '#6a1044',
        1300: '#430a2b',
        1400: '#1c0412',
      },
      radiantAnemoneO: {
        50: '#fcf4fa',
        100: '#faeaf6',
        200: '#f8e0f2',
        300: '#f4cbea',
        400: '#efb2e0',
        500: '#ea98d6',
        600: '#e57fcc',
        700: '#e066c1',
        800: '#db4cb7',
        900: '#d633ad',
        950: '#d119a3',
        1000: '#cc0099',
        1100: '#a10078',
        1200: '#750058',
        1300: '#4a0037',
        1400: '#1f0017',
      },
    },
    cyan: {
      cyan: {
        50: '#e5fbf9',
        100: '#a4f8ff',
        200: '#73f4ff',
        300: '#3ed2de',
        400: '#29bac6',
        500: '#169ca7',
        600: '#007a84',
        700: '#115469',
        800: '#043c4d',
        900: '#032833',
        950: '#01141a',
        1000: '#000a0d',
      },
      skywakeCyanA: {
        50: '#0099cc0a',
        100: '#0099cc14',
        200: '#0099cc1f',
        300: '#0099cc33',
        400: '#0099cc4d',
        500: '#0099cc66',
        600: '#0099cc80',
        700: '#0099cc99',
        800: '#0099ccb2',
        900: '#0099cccc',
        950: '#0099cce5',
        1000: '#0099cc',
      },
      surfaceBeaconA: {
        50: '#0088990a',
        100: '#00889914',
        200: '#0088991f',
        300: '#00889933',
        400: '#0088994d',
        500: '#00889966',
        600: '#00889980',
        700: '#00889999',
        800: '#008899b2',
        900: '#008899cc',
        950: '#008899e5',
        1000: '#008899',
      },
      skywakeCyanO: {
        50: '#f4fafc',
        100: '#eaf6fa',
        200: '#e0f2f8',
        300: '#cbeaf4',
        400: '#b2e0ef',
        500: '#98d6ea',
        600: '#7fcce5',
        700: '#66c1e0',
        800: '#4cb7db',
        900: '#33add6',
        950: '#19a3d1',
        1000: '#0099cc',
        1100: '#007fa9',
        1200: '#006587',
        1300: '#004b64',
        1400: '#003141',
        1500: '#00171f',
      },
      surfaceBeaconO: {
        50: '#f4f9fa',
        100: '#eaf5f6',
        200: '#e0f0f2',
        300: '#cbe6ea',
        400: '#b2dbe0',
        500: '#98cfd6',
        600: '#7fc3cc',
        700: '#66b7c1',
        800: '#4cabb7',
        900: '#33a0ad',
        950: '#1994a3',
        1000: '#008899',
        1100: '#006b78',
        1200: '#004e58',
        1300: '#003137',
        1400: '#001417',
      },
    },
    teal: {
      sunsparkOasis: {
        50: '#e5fbf9',
        100: '#91eddc',
        200: '#56e3c9',
        300: '#35c6ab',
        400: '#14b597',
        500: '#09a084',
        600: '#006a68',
        700: '#005553',
        800: '#004140',
        900: '#02231f',
        950: '#011412',
        1000: '#010a09',
      },
      sunsparkOasisA: {
        50: '#00dcc20a',
        100: '#00dcc214',
        200: '#00dcc21f',
        300: '#00dcc233',
        400: '#00dcc24d',
        500: '#00dcc266',
        600: '#00dcc280',
        700: '#00dcc299',
        800: '#00dcc2b2',
        900: '#00dcc2cc',
        950: '#00dcc2e5',
        1000: '#00dcc2',
      },
      shallowCurrentTealA: {
        0: '#00996600',
        50: '#0099660a',
        100: '#00996614',
        200: '#0099661f',
        300: '#00996633',
        400: '#0099664d',
        500: '#00996666',
        600: '#00996680',
        700: '#00996699',
        800: '#009966b2',
        900: '#009966cc',
        950: '#009966e5',
        1000: '#009966',
      },
      cryoWakeA: {
        0: '#80fcff00',
        50: '#80fcff0a',
        100: '#80fcff14',
        200: '#80fcff1f',
        300: '#80fcff33',
        400: '#80fcff4d',
        500: '#80fcff66',
        600: '#80fcff80',
        700: '#80fcff99',
        800: '#80fcffb2',
        900: '#80fcffcc',
        950: '#80fcffe5',
        1000: '#80fcff',
      },
      sunsparkOasis2: {
        50: '#e5fbf9',
        100: '#bff6f0',
        200: '#80ede0',
        300: '#40e5d1',
        400: '#00dcc2',
        500: '#00c2ab',
        600: '#019a88',
        700: '#017365',
        800: '#024b42',
        900: '#02231f',
      },
      sunsparkOasis3: {
        50: '#e5fbf9',
        100: '#bff6f0',
        200: '#80ede0',
        300: '#40e5d1',
        400: '#00dcc2',
        500: '#00c2ab',
        600: '#019a88',
        700: '#017365',
        800: '#024b42',
        900: '#02231f',
      },
      mintyA: {
        0: '#006a680a',
        50: '#006a680a',
        100: '#006a6814',
        200: '#006a681f',
        300: '#006a6833',
        400: '#006a684d',
        500: '#006a6866',
        600: '#006a6880',
        700: '#006a6899',
        800: '#006a68b2',
        900: '#006a68cc',
        950: '#006a68e5',
        1000: '#006a68',
      },
      frozenExpanseA: {
        0: '#9effff0a',
        50: '#9effff0a',
        100: '#9effff14',
        200: '#9effff1f',
        300: '#9effff33',
        400: '#9effff4d',
        500: '#9effff66',
        600: '#9effff80',
        700: '#9effff99',
        800: '#9effffb2',
        900: '#9effffcc',
        950: '#9effffe5',
        1000: '#9effff',
      },
      cryoWakeO: {
        50: '#f9fefe',
        100: '#f4fefe',
        200: '#effefe',
        300: '#e5fefe',
        400: '#d8fdfe',
        500: '#ccfdfe',
        600: '#bffdff',
        700: '#b2fdff',
        800: '#a6fdff',
        900: '#99fcff',
        950: '#8dfcff',
        1000: '#80fcff',
        1100: '#4afbff',
        1200: '#13f9ff',
        1300: '#00d7dc',
        1400: '#00a2a6',
        1500: '#006d70',
        1600: '#003839',
      },
      frozenExpanseO: {
        50: '#fafefe',
        100: '#f6fefe',
        200: '#f2fefe',
        300: '#ebfefe',
        400: '#e1fefe',
        500: '#d8fefe',
        600: '#ceffff',
        700: '#c4ffff',
        800: '#bbffff',
        900: '#b1ffff',
        950: '#a8ffff',
        1000: '#9effff',
        1100: '#63ffff',
        1200: '#29ffff',
        1300: '#00eded',
        1400: '#00b3b3',
        1500: '#007878',
        1600: '#003e3e',
      },
      mintyO: {
        50: '#f4f8f8',
        100: '#eaf2f2',
        200: '#e0ecec',
        300: '#cbe0e0',
        400: '#b2d2d1',
        500: '#98c3c2',
        600: '#7fb4b3',
        700: '#66a5a4',
        800: '#4c9695',
        900: '#338886',
        950: '#197977',
        1000: '#006a68',
        1100: '#005352',
        1200: '#003d3c',
        1300: '#002626',
        1400: '#001010',
      },
      shallowCurrentTealO: {
        50: '#f4faf8',
        100: '#eaf6f2',
        200: '#e0f2ec',
        300: '#cbeae0',
        400: '#b2e0d0',
        500: '#98d6c1',
        600: '#7fccb2',
        700: '#66c1a3',
        800: '#4cb794',
        900: '#33ad84',
        950: '#19a375',
        1000: '#009966',
        1100: '#007f55',
        1200: '#006543',
        1300: '#004b32',
        1400: '#003121',
        1500: '#00170f',
      },
      sunsparkOasisO: {
        50: '#f4fdfc',
        100: '#eafbf9',
        200: '#e0faf7',
        300: '#cbf7f2',
        400: '#b2f4ec',
        500: '#98f0e6',
        600: '#7fede0',
        700: '#66eada',
        800: '#4ce6d4',
        900: '#33e3ce',
        950: '#19dfc8',
        1000: '#00dcc2',
        1100: '#00bda7',
        1200: '#009e8b',
        1300: '#007f70',
        1400: '#005f54',
        1500: '#004039',
        1600: '#00211d',
      },
    },
  },
  neutrals: {
    clear: '#ffffff00',
    celestialVeilA: {
      50: '#e7f2fc0a',
      100: '#e7f2fc14',
      200: '#e7f2fc1f',
      300: '#e7f2fc33',
      400: '#e7f2fc4d',
      500: '#e7f2fc66',
      600: '#e7f2fc80',
      700: '#e7f2fc99',
      800: '#e7f2fcb2',
      900: '#e7f2fccc',
      950: '#e7f2fce5',
      1000: '#e7f2fc',
    },
    echoLightA: {
      50: '#f5f5f50a',
      100: '#f5f5f514',
      200: '#f5f5f51f',
      300: '#f5f5f533',
      400: '#f5f5f54d',
      500: '#f5f5f566',
      600: '#f5f5f580',
      700: '#f5f5f599',
      800: '#f5f5f5b2',
      900: '#f5f5f5cc',
      950: '#f5f5f5e5',
      1000: '#f5f5f5',
    },
    grayBlueA: {
      0: '#36465e00',
      25: '#36465e05',
      50: '#36465e0a',
      100: '#36465e14',
      200: '#36465e1f',
      300: '#36465e33',
      400: '#36465e4d',
      500: '#36465e66',
      600: '#36465e80',
      700: '#36465e99',
      800: '#36465eb2',
      900: '#36465ecc',
      950: '#36465ee5',
      1000: '#36465e',
    },
    darkstarCoreA: {
      50: '#0404060a',
      100: '#04040614',
      200: '#0404061f',
      300: '#04040633',
      400: '#0404064d',
      500: '#04040666',
      600: '#04040680',
      700: '#04040699',
      800: '#040406b2',
      900: '#040406cc',
      950: '#040406e5',
      1000: '#040406',
    },
    darthAbyssA: {
      50: '#0101050a',
      100: '#01010514',
      200: '#0101051f',
      300: '#01010533',
      400: '#0101054d',
      500: '#01010566',
      600: '#01010580',
      700: '#01010599',
      800: '#010105b2',
      900: '#010105cc',
      950: '#010105e5',
      1000: '#010105',
    },
    nightbladeA: {
      50: '#0101010a',
      100: '#01010114',
      200: '#0101011f',
      300: '#01010133',
      400: '#0101014d',
      500: '#01010166',
      600: '#01010180',
      700: '#01010199',
      800: '#010101b2',
      900: '#010101cc',
      950: '#010101e5',
      1000: '#010101',
    },
    kyberCrystalA: {
      50: '#f5f5f50a',
      100: '#f5f5f514',
      200: '#f5f5f51f',
      300: '#f5f5f533',
      400: '#f5f5f54d',
      500: '#f5f5f566',
      600: '#f5f5f580',
      700: '#f5f5f599',
      800: '#f5f5f5b2',
      900: '#f5f5f5cc',
      950: '#f5f5f5e5',
      1000: '#f5f5f5',
    },
    duskwraithA: {
      50: '#1b20230a',
      100: '#09090a14',
      200: '#09090a1f',
      300: '#09090a33',
      400: '#09090a4d',
      500: '#09090a66',
      600: '#09090a80',
      700: '#09090a99',
      800: '#09090ab2',
      900: '#09090acc',
      950: '#09090ae5',
      1000: '#09090a',
    },
    forceDusk: {
      50: '#f7f7fa',
      100: '#e6e6f0',
      200: '#c9c9d6',
      300: '#77778c',
      400: '#5c5c73',
      500: '#3a3a4d',
      600: '#242433',
      700: '#191927',
      800: '#11111f',
      900: '#02020f',
      950: '#010105',
      1000: '#000003',
    },
    gray: {
      50: '#f7f7fa',
      100: '#ebebeb',
      200: '#dedede',
      300: '#bdbdbd',
      400: '#858585',
      500: '#716f6f',
      600: '#4f4f4f',
      700: '#333333',
      800: '#1f1f20',
      900: '#141414',
      950: '#010105',
      1000: '#000003',
    },
    pureBlack: {
      50: '#0000000a',
      100: '#00000014',
      200: '#0000001f',
      300: '#00000033',
      400: '#0000004d',
      500: '#00000066',
      600: '#00000080',
      700: '#00000099',
      800: '#000000b2',
      900: '#000000cc',
      950: '#000000e5',
      1000: '#000000',
    },
    pureWhite: {
      50: '#ffffff0a',
      100: '#ffffff14',
      200: '#ffffff1f',
      300: '#ffffff33',
      400: '#ffffff4d',
      500: '#ffffff66',
      600: '#ffffff80',
      700: '#ffffff99',
      800: '#ffffffb2',
      900: '#ffffffcc',
      950: '#ffffffe5',
      1000: '#ffffff',
    },
    radiantA: {
      50: '#f8f9fc0a',
      100: '#f8f9fc14',
      200: '#f8f9fc1f',
      300: '#f8f9fc33',
      400: '#f8f9fc4d',
      500: '#f8f9fc66',
      600: '#f8f9fc80',
      700: '#f8f9fc99',
      800: '#f8f9fcb2',
      900: '#f8f9fccc',
      950: '#f8f9fce5',
      1000: '#f8f9fc',
    },
    moonwakeMistA: {
      50: '#e1efff0a',
      100: '#e1efff14',
      200: '#e1efff1f',
      300: '#e1efff33',
      400: '#e1efff4d',
      500: '#e1efff66',
      600: '#e1efff80',
      700: '#e1efff99',
      800: '#e1efffb2',
      900: '#e1efffcc',
      950: '#e1efffe5',
      1000: '#e1efff',
    },
    asteroidFoamA: {
      50: '#cccccc0a',
      100: '#cccccc14',
      200: '#cccccc1f',
      300: '#cccccc33',
      400: '#cccccc4d',
      500: '#cccccc66',
      600: '#cccccc80',
      700: '#cccccc99',
      800: '#ccccccb2',
      900: '#cccccccc',
      950: '#cccccce5',
      1000: '#cccccc',
    },
    vacuumIceA: {
      50: '#f8f8f80a',
      100: '#f8f8f814',
      200: '#f8f8f81f',
      300: '#f8f8f833',
      400: '#f8f8f84d',
      500: '#f8f8f866',
      600: '#f8f8f880',
      700: '#f8f8f899',
      800: '#f8f8f8b2',
      900: '#f8f8f8cc',
      950: '#f8f8f8e5',
      1000: '#f8f8f8',
    },
    deepOrbitSlateA: {
      50: '#8996a80a',
      100: '#8996a814',
      200: '#8996a81f',
      300: '#8996a833',
      400: '#8996a84d',
      500: '#8996a866',
      600: '#8996a880',
      700: '#8996a899',
      800: '#8996a8b2',
      900: '#8996a8cc',
      950: '#8996a8e5',
      1000: '#8996a8',
    },
    docksideSlateA: {
      50: '#5555550a',
      100: '#55555514',
      200: '#5555551f',
      300: '#55555533',
      400: '#5555554d',
      500: '#55555566',
      600: '#55555580',
      700: '#55555599',
      800: '#555555b2',
      900: '#555555cc',
      950: '#555555e5',
      1000: '#555555',
    },
    atmosphericSteelA: {
      50: '#5f6c7e0a',
      100: '#5f6c7e14',
      200: '#5f6c7e1f',
      300: '#5f6c7e33',
      400: '#5f6c7e4d',
      500: '#5f6c7e66',
      600: '#5f6c7e80',
      700: '#5f6c7e99',
      800: '#5f6c7eb2',
      900: '#5f6c7ecc',
      950: '#5f6c7ee5',
      1000: '#5f6c7e',
    },
    upperOrbitFogA: {
      50: '#7a8ea80a',
      100: '#7a8ea814',
      200: '#7a8ea81f',
      300: '#7a8ea833',
      400: '#7a8ea84d',
      500: '#7a8ea866',
      600: '#7a8ea880',
      700: '#7a8ea899',
      800: '#7a8ea8b2',
      900: '#7a8ea8cc',
      950: '#7a8ea8e5',
      1000: '#7a8ea8',
    },
    voidInkA: {
      50: '#000e1a0a',
      100: '#000e1a14',
      200: '#000e1a1f',
      300: '#000e1a33',
      400: '#000e1a4d',
      500: '#000e1a66',
      600: '#000e1a80',
      700: '#000e1a99',
      800: '#000e1ab2',
      900: '#000e1acc',
      950: '#000e1ae5',
      1000: '#000e1a',
    },
    echoLightO: {
      50: '#fefefe',
      100: '#fdfdfd',
      200: '#fdfdfd',
      300: '#fcfcfc',
      400: '#fbfbfb',
      500: '#fafafa',
      600: '#fafafa',
      700: '#f9f9f9',
      800: '#f8f8f8',
      900: '#f7f7f7',
      950: '#f6f6f6',
      1000: '#f5f5f5',
      1100: '#d2d2d2',
      1200: '#b0b0b0',
      1300: '#8d8d8d',
      1400: '#6a6a6a',
      1500: '#474747',
      1600: '#252525',
    },
    duskwraithO: {
      50: '#f4f4f4',
      100: '#eaeaea',
      200: '#e1e1e1',
      300: '#cdcdcd',
      400: '#b5b5b5',
      500: '#9c9c9c',
      600: '#848484',
      700: '#6b6b6c',
      800: '#535353',
      900: '#3a3a3b',
      950: '#222222',
      1000: '#09090a',
    },
    asteroidFoamO: {
      50: '#fcfcfc',
      100: '#fafafa',
      200: '#f8f8f8',
      300: '#f4f4f4',
      400: '#efefef',
      500: '#eaeaea',
      600: '#e5e5e5',
      700: '#e0e0e0',
      800: '#dbdbdb',
      900: '#d6d6d6',
      950: '#d1d1d1',
      1000: '#cccccc',
      1100: '#afafaf',
      1200: '#929292',
      1300: '#757575',
      1400: '#585858',
      1500: '#3c3c3c',
      1600: '#1f1f1f',
    },
    atmosphericSteelO: {
      50: '#f8f8f9',
      100: '#f1f2f4',
      200: '#ebecef',
      300: '#dee1e4',
      400: '#ced2d8',
      500: '#bec4cb',
      600: '#afb5be',
      700: '#9fa6b1',
      800: '#8f98a4',
      900: '#7f8998',
      950: '#6f7b8b',
      1000: '#5f6c7e',
      1100: '#4f5a69',
      1200: '#3f4753',
      1300: '#2f353e',
      1400: '#1e2328',
      1500: '#0e1013',
    },
    celestialVeilO: {
      50: '#fdfefe',
      100: '#fcfdfe',
      200: '#fbfdfe',
      300: '#f9fcfe',
      400: '#f7fafd',
      500: '#f5f9fd',
      600: '#f3f8fd',
      700: '#f0f7fd',
      800: '#eef6fd',
      900: '#ecf4fc',
      950: '#e9f3fc',
      1000: '#e7f2fc',
      1100: '#aad1f4',
      1200: '#6db0ed',
      1300: '#318fe5',
      1400: '#176dba',
      1500: '#10497d',
      1600: '#082640',
    },
    darkstarCoreO: {
      50: '#f4f4f4',
      100: '#eaeaea',
      200: '#e0e0e0',
      300: '#cccccc',
      400: '#b3b3b4',
      500: '#9a9a9b',
      600: '#818182',
      700: '#686869',
      800: '#4f4f50',
      900: '#363638',
      950: '#1d1d1f',
      1000: '#040406',
    },
    darthAbyssO: {
      50: '#f4f4f4',
      100: '#eaeaea',
      200: '#e0e0e0',
      300: '#cbcbcc',
      400: '#b2b2b3',
      500: '#99999a',
      600: '#7f7f82',
      700: '#666669',
      800: '#4d4d50',
      900: '#333337',
      950: '#1a1a1e',
      1000: '#010105',
    },
    deepOrbitSlateO: {
      50: '#f9fafb',
      100: '#f5f6f7',
      200: '#f0f2f4',
      300: '#e7e9ed',
      400: '#dbdfe4',
      500: '#cfd4dc',
      600: '#c4cad3',
      700: '#b8c0ca',
      800: '#acb5c2',
      900: '#a0abb9',
      950: '#95a0b1',
      1000: '#8996a8',
      1100: '#708096',
      1200: '#5d6b7e',
      1300: '#4a5665',
      1400: '#38404c',
      1500: '#262b33',
      1600: '#13161a',
    },
    docksideSlateO: {
      50: '#f7f7f7',
      100: '#f0f0f0',
      200: '#eaeaea',
      300: '#dcdcdc',
      400: '#cbcbcb',
      500: '#bababa',
      600: '#aaaaaa',
      700: '#999999',
      800: '#888888',
      900: '#777777',
      950: '#666666',
      1000: '#555555',
      1100: '#434343',
      1200: '#313131',
      1300: '#1f1f1f',
      1400: '#0d0d0d',
    },
    grayBlueO: {
      50: '#f6f7f8',
      100: '#eeeff1',
      200: '#e6e8eb',
      300: '#d6d9de',
      400: '#c2c7ce',
      500: '#aeb4be',
      600: '#9aa2ae',
      700: '#86909e',
      800: '#727d8e',
      900: '#5e6b7e',
      950: '#4a586e',
      1000: '#36465e',
      1100: '#273243',
      1200: '#171e29',
      1300: '#080b0e',
    },
    kyberCrystalO: {
      50: '#fefefe',
      100: '#fdfdfd',
      200: '#fdfdfd',
      300: '#fcfcfc',
      400: '#fbfbfb',
      500: '#fafafa',
      600: '#fafafa',
      700: '#f9f9f9',
      800: '#f8f8f8',
      900: '#f7f7f7',
      950: '#f6f6f6',
      1000: '#f5f5f5',
      1100: '#d2d2d2',
      1200: '#b0b0b0',
      1300: '#8d8d8d',
      1400: '#6a6a6a',
      1500: '#474747',
      1600: '#252525',
    },
    moonwakeMistO: {
      50: '#fdfdfe',
      100: '#fcfdfe',
      200: '#fbfcfe',
      300: '#f8fbfe',
      400: '#f5fafe',
      500: '#f2f8fe',
      600: '#f0f7ff',
      700: '#edf5ff',
      800: '#eaf4ff',
      900: '#e7f2ff',
      950: '#e4f1ff',
      1000: '#e1efff',
      1100: '#9dcbff',
      1200: '#59a6ff',
      1300: '#1582ff',
      1400: '#0061d0',
      1500: '#00418c',
      1600: '#002248',
    },
    nightbladeO: {
      50: '#f4f4f4',
      100: '#eaeaea',
      200: '#e0e0e0',
      300: '#cbcbcb',
      400: '#b2b2b2',
      500: '#999999',
      600: '#808080',
      700: '#666666',
      800: '#4d4d4d',
      900: '#343434',
      950: '#1a1a1a',
      1000: '#010101',
    },
    radiantO: {
      50: '#fefefe',
      100: '#fefefe',
      200: '#fdfdfe',
      300: '#fdfdfe',
      400: '#fcfdfd',
      500: '#fcfcfd',
      600: '#fbfcfd',
      700: '#fafbfd',
      800: '#fafbfd',
      900: '#f9fafc',
      950: '#f9fafc',
      1000: '#f8f9fc',
      1100: '#c6cee7',
      1200: '#95a4d1',
      1300: '#637abc',
      1400: '#415798',
      1500: '#2c3a66',
      1600: '#171e35',
    },
    upperOrbitFogO: {
      50: '#f9fafb',
      100: '#f3f5f7',
      200: '#eef1f4',
      300: '#e4e8ed',
      400: '#d6dce4',
      500: '#c9d1dc',
      600: '#bcc6d3',
      700: '#afbbca',
      800: '#a2b0c2',
      900: '#94a4b9',
      950: '#8799b1',
      1000: '#7a8ea8',
      1100: '#5f7592',
      1200: '#4c5d74',
      1300: '#384556',
      1400: '#252d38',
      1500: '#11151a',
    },
    vacuumIceO: {
      50: '#fefefe',
      100: '#fefefe',
      200: '#fdfdfd',
      300: '#fdfdfd',
      400: '#fcfcfc',
      500: '#fcfcfc',
      600: '#fbfbfb',
      700: '#fafafa',
      800: '#fafafa',
      900: '#f9f9f9',
      950: '#f9f9f9',
      1000: '#f8f8f8',
      1100: '#d5d5d5',
      1200: '#b2b2b2',
      1300: '#8f8f8f',
      1400: '#6b6b6b',
      1500: '#484848',
      1600: '#252525',
    },
    voidInkO: {
      50: '#f4f4f5',
      100: '#eaebec',
      200: '#e0e1e3',
      300: '#cbced0',
      400: '#b2b6ba',
      500: '#989ea3',
      600: '#7f868c',
      700: '#666e75',
      800: '#4c565e',
      900: '#333e48',
      950: '#192631',
      1000: '#000e1a',
      1100: '#000204',
    },
  },
  special: {
    code1: {
      200: '#e1efff',
      300: '#96a3b2',
      400: '#8592a0',
      500: '#74818f',
      600: '#6a7685',
      700: '#5b6775',
      800: '#4e5967',
    },
    code2: {
      200: '#ff729b',
      300: '#ff628c',
      400: '#f55883',
      500: '#e04573',
      600: '#d43869',
      700: '#c1235a',
      800: '#b0044c',
    },
    code3: {
      200: '#ffdd00',
      300: '#c09e00',
      400: '#af8d00',
      500: '#9d7b00',
      600: '#927000',
      700: '#826100',
      800: '#745200',
    },
    code4: {
      200: '#ff9d00',
      300: '#ea8a00',
      400: '#d77800',
      500: '#c46700',
      600: '#b85c00',
      700: '#a74b00',
      800: '#973c00',
    },
    code5: {
      200: '#ffee80',
      300: '#b3a22e',
      400: '#a29110',
      500: '#918000',
      600: '#867500',
      700: '#776500',
      800: '#685700',
    },
    code6: {
      200: '#3ad900',
      300: '#00bc00',
      400: '#00a800',
      500: '#009500',
      600: '#008900',
      700: '#007700',
      800: '#006700',
    },
    code7: {
      200: '#38a7ff',
      300: '#1a92ff',
      400: '#0088ff',
      500: '#007ef4',
      600: '#0072e7',
      700: '#0061d4',
      800: '#0051c3',
    },
    code8: {
      200: '#80ffbb',
      300: '#2cb778',
      400: '#00a567',
      500: '#009356',
      600: '#00874b',
      700: '#00763c',
      800: '#00662d',
    },
    code9: {
      200: '#cccccc',
      300: '#a1a1a1',
      400: '#909090',
      500: '#808080',
      600: '#757575',
      700: '#666666',
      800: '#585858',
    },
    code10: {
      200: '#ff80e1',
      300: '#f173d4',
      400: '#de61c2',
      500: '#cb4fb0',
      600: '#be44a5',
      700: '#ad3294',
      800: '#9c1f85',
    },
    code11: {
      200: '#f8f8f8',
      300: '#a1a1a1',
      400: '#909090',
      500: '#808080',
      600: '#757575',
      700: '#666666',
      800: '#585858',
    },
    code12: {
      200: '#80fcff',
      300: '#26b2b6',
      400: '#00a1a4',
      500: '#008e92',
      600: '#008286',
      700: '#007276',
      800: '#006267',
    },
    code13: {
      200: '#9eff80',
      300: '#57b635',
      400: '#45a41d',
      500: '#329300',
      600: '#258700',
      700: '#097700',
      800: '#006700',
    },
    code14: {
      200: '#80ff82',
      300: '#32b93c',
      400: '#13a727',
      500: '#009506',
      600: '#008900',
      700: '#007700',
      800: '#006700',
    },
    code15: {
      200: '#80ffc2',
      300: '#2bb77e',
      400: '#00a56e',
      500: '#00925c',
      600: '#008752',
      700: '#007642',
      800: '#006634',
    },
    code16: {
      200: '#edef7d',
      300: '#a6a72e',
      400: '#959512',
      500: '#848400',
      600: '#7a7900',
      700: '#6b6900',
      800: '#5d5b00',
    },
    code17: {
      200: '#ffb054',
      300: '#dd912f',
      400: '#cb7f14',
      500: '#b86e00',
      600: '#ad6300',
      700: '#9b5300',
      800: '#8c4500',
    },
    code18: {
      200: '#eb939a',
      300: '#e08990',
      400: '#cd7880',
      500: '#bb676f',
      600: '#af5d65',
      700: '#9e4d56',
      800: '#8e3f49',
    },
    code19: {
      200: '#ff795d',
      300: '#ff553b',
      400: '#ff1e00',
      500: '#fc1800',
      600: '#ec0000',
      700: '#cf0000',
      800: '#b50000',
    },
    code20: {
      200: '#95a3b5',
      300: '#8996a8',
      400: '#8592a3',
      500: '#748192',
      600: '#6a7687',
      700: '#5b6778',
      800: '#4d596a',
    },
    code21: {
      200: '#afc4db',
      300: '#8fa4ba',
      400: '#7f92a8',
      500: '#6e8297',
      600: '#64778c',
      700: '#55687c',
      800: '#485a6e',
    },
    code22: {
      200: '#96a5a0',
      300: '#85938f',
      400: '#75837f',
      500: '#73817d',
      600: '#6a7874',
      700: '#5b6965',
      800: '#4e5b57',
    },
    code23: {
      200: '#9effff',
      300: '#4db0b0',
      400: '#399f9f',
      500: '#228d8e',
      600: '#0d8283',
      700: '#007273',
      800: '#006364',
    },
    code24: {
      200: '#ffb454',
      300: '#db922b',
      400: '#c8810b',
      500: '#b67000',
      600: '#aa6500',
      700: '#995500',
      800: '#894600',
    },
    code25: {
      200: '#5fe461',
      300: '#2bba34',
      400: '#00a81c',
      500: '#009500',
      600: '#008900',
      700: '#007700',
      800: '#006700',
    },
    code26: {
      200: '#9df39f',
      300: '#5fb363',
      400: '#4ea252',
      500: '#3c9042',
      600: '#308637',
      700: '#1c7627',
      800: '#016717',
    },
    code27: {
      200: '#f6f090',
      300: '#aba444',
      400: '#9a9331',
      500: '#89821a',
      600: '#7f7803',
      700: '#6f6800',
      800: '#615900',
    },
    code28: {
      200: '#f6aa11',
      300: '#dc9200',
      400: '#ca8000',
      500: '#b76f00',
      600: '#ac6400',
      700: '#9b5400',
      800: '#8b4500',
    },
    code29: {
      200: '#edf080',
      300: '#a5a732',
      400: '#949518',
      500: '#848400',
      600: '#797900',
      700: '#6a6900',
      800: '#5c5b00',
    },
    code30: {
      200: '#c1afff',
      300: '#a896e4',
      400: '#9785d2',
      500: '#8774bf',
      600: '#7c6ab4',
      700: '#6d5aa3',
      800: '#5f4c94',
    },
    code31: {
      200: '#b8ffd9',
      300: '#6baf8c',
      400: '#5a9e7c',
      500: '#488d6b',
      600: '#3d8261',
      700: '#2d7252',
      800: '#1c6445',
    },
    code32: {
      200: '#c8e4fd',
      300: '#8aa5bc',
      400: '#7993aa',
      500: '#698399',
      600: '#5f788e',
      700: '#50697e',
      800: '#425b70',
    },
    code33: {
      200: '#73a5da',
      300: '#6394c8',
      400: '#5283b6',
      500: '#4878ab',
      600: '#39699a',
      700: '#2b5b8b',
      800: '#2a5a8a',
    },
    code34: {
      200: '#ff7397',
      300: '#f8567d',
      400: '#e3426d',
      500: '#d73563',
      600: '#c41e54',
      700: '#c41e54',
      800: '#b10045',
    },
    code35: {
      200: '#b8a600',
      300: '#b4a200',
      400: '#a39100',
      500: '#928000',
      600: '#877500',
      700: '#786500',
      800: '#6a5700',
    },
    code36: {
      200: '#e78c30',
      300: '#d47a15',
      400: '#cc7300',
      500: '#c16900',
      600: '#b55e00',
      700: '#a34e00',
      800: '#933f00',
    },
    code37: {
      200: '#b5a221',
      300: '#a89500',
      400: '#a49100',
      500: '#927f00',
      600: '#887500',
      700: '#786500',
      800: '#6a5600',
    },
    code38: {
      200: '#50b738',
      300: '#3da522',
      400: '#2a9500',
      500: '#289300',
      600: '#178800',
      700: '#007700',
      800: '#006700',
    },
    code39: {
      200: '#51a5ff',
      300: '#3c91fa',
      400: '#297fe7',
      500: '#1a74db',
      600: '#0066cc',
      700: '#0064c9',
      800: '#0054b8',
    },
    code40: {
      200: '#39b681',
      300: '#1fa470',
      400: '#009966',
      500: '#009260',
      600: '#008655',
      700: '#007545',
      800: '#006637',
    },
    code41: {
      200: '#a1a1a1',
      300: '#909090',
      400: '#808080',
      500: '#757575',
      600: '#666666',
      700: '#585858',
      800: '#555555',
    },
    code42: {
      200: '#ff65db',
      300: '#f846bf',
      400: '#e32ead',
      500: '#d71ca2',
      600: '#cc0099',
      700: '#c1008f',
      800: '#a8007a',
    },
    code43: {
      200: '#f5806b',
      300: '#e16e5a',
      400: '#ce5d4a',
      500: '#c2523f',
      600: '#b04230',
      700: '#9f3221',
      800: '#800f00',
    },
    code44: {
      200: '#8ea4b8',
      300: '#7d93a6',
      400: '#6d8295',
      500: '#63788a',
      600: '#54687a',
      700: '#465a6c',
      800: '#223545',
    },
    code45: {
      200: '#2eade1',
      300: '#089bce',
      400: '#0099cc',
      500: '#0089bb',
      600: '#007daf',
      700: '#006c9d',
      800: '#005d8d',
    },
    code46: {
      200: '#61b509',
      300: '#5fb300',
      400: '#50a300',
      500: '#3e9100',
      600: '#338600',
      700: '#217600',
      800: '#0c6700',
    },
    code47: {
      200: '#1abb17',
      300: '#00b300',
      400: '#00a800',
      500: '#009500',
      600: '#008900',
      700: '#007700',
      800: '#006700',
    },
    code48: {
      200: '#2ab685',
      300: '#00a676',
      400: '#00a575',
      500: '#009263',
      600: '#008659',
      700: '#007549',
      800: '#00663b',
    },
    code49: {
      200: '#b9a113',
      300: '#b39b00',
      400: '#a78f00',
      500: '#967e00',
      600: '#8b7300',
      700: '#7b6400',
      800: '#6d5500',
    },
    code50: {
      200: '#e28e2b',
      300: '#cf7d0a',
      400: '#cc7a00',
      500: '#bc6c00',
      600: '#b16100',
      700: '#9f5100',
      800: '#8f4200',
    },
    code51: {
      200: '#ef818b',
      300: '#dc6f7b',
      400: '#c85e6a',
      500: '#bc5360',
      600: '#b54d5a',
      700: '#aa4351',
      800: '#9a3444',
    },
    code52: {
      200: '#ff7960',
      300: '#ff543e',
      400: '#eb3f2a',
      500: '#de301d',
      600: '#cc1600',
      700: '#cb1500',
      800: '#b50000',
    },
    code53: {
      200: '#95a3b6',
      300: '#8492a5',
      400: '#738193',
      500: '#697689',
      600: '#5f6c7e',
      700: '#5a6779',
      800: '#4d596b',
    },
    code54: {
      200: '#8fa3be',
      300: '#7e92ac',
      400: '#7a8ea8',
      500: '#6e819b',
      600: '#637790',
      700: '#556780',
      800: '#475a72',
    },
    code55: {
      200: '#98a4a2',
      300: '#879391',
      400: '#768280',
      500: '#6c7875',
      600: '#5d6866',
      700: '#4f5a58',
      800: '#4a5553',
    },
    code56: {
      200: '#44afc1',
      300: '#2e9eaf',
      400: '#0f8c9d',
      500: '#008899',
      600: '#008192',
      700: '#007081',
      800: '#006172',
    },
    code57: {
      200: '#df9028',
      300: '#cd7f02',
      400: '#cc7e00',
      500: '#ba6d00',
      600: '#ae6200',
      700: '#9d5200',
      800: '#8d4400',
    },
    code58: {
      200: '#52b648',
      300: '#3fa435',
      400: '#3aa030',
      500: '#2a9320',
      600: '#1b880e',
      700: '#007700',
      800: '#006700',
    },
    code59: {
      200: '#63b364',
      300: '#51a153',
      400: '#4d9d4f',
      500: '#409043',
      600: '#348538',
      700: '#227528',
      800: '#0c6718',
    },
    code60: {
      200: '#b5a208',
      300: '#b3a000',
      400: '#a49100',
      500: '#927f00',
      600: '#887500',
      700: '#786500',
      800: '#6a5600',
    },
    code61: {
      200: '#e08f30',
      300: '#ce7e14',
      400: '#c67700',
      500: '#bb6d00',
      600: '#af6200',
      700: '#9e5200',
      800: '#8e4300',
    },
    code62: {
      200: '#8da4b5',
      300: '#7d93a4',
      400: '#6c8393',
      500: '#627888',
      600: '#536978',
      700: '#465b6a',
      800: '#000e1a',
    },
    code63: {
      200: '#aa90ff',
      300: '#977bff',
      400: '#876aeb',
      500: '#7d5fdf',
      600: '#6e4ecd',
      700: '#6d4dcc',
      800: '#613fbd',
    },
    code64: {
      200: '#54a6f8',
      300: '#4294e5',
      400: '#2e83d2',
      500: '#2178c6',
      600: '#0468b5',
      700: '#0066b3',
      800: '#0059a5',
    },
  },

  // TODO: Old tokens, remove once completely migrated to new tokens
  // Base colors
  transparent: 'transparent',
  current: 'currentColor',
  inherit: 'inherit',
  white: '#FFFFFF',
  black: '#000000',

  // White variants
  'white-100': '#FEFEFE',
  'white-200': '#F6F6F6',

  // Gray variants
  'gray-100': '#EBEBEB',
  'gray-200': '#DEDEDE',
  'gray-300': '#BDBDBD',
  'gray-400': '#858585',
  'gray-500': '#716F6F',
  'gray-600': '#4F4F4F',
  'gray-700': '#333333',
  'gray-800': '#1F1F20',

  // Navy blue variants
  'navyblue-100': '#86A3C3',
  'navyblue-200': '#4F627C',
  'navyblue-300': '#3E516B',
  'navyblue-350': '#30415C',
  'navyblue-400': '#23344E',
  'navyblue-500': '#1D2A3F',
  'navyblue-600': '#192231',
  'navyblue-700': '#141C2A',
  'navyblue-800': '#0F1623',
  'navyblue-900': '#0A0F1A',

  // Blue variants
  'blue-50': '#E6EFFA',
  'blue-100': '#ACE2FF',
  'blue-200': '#7FD3FF',
  'blue-300': '#11A5F3',
  'blue-400': '#0081C5',
  'blue-500': '#0B5297',
  'blue-600': '#073E73',
  'blue-700': '#042E57',
  'blue-800': '#032241',

  // Teal variants
  'teal-100': '#91EDDC',
  'teal-200': '#56E3C9',
  'teal-300': '#35C6AB',
  'teal-400': '#14B597',
  'teal-500': '#09A084',
  'teal-600': '#006A68',
  'teal-700': '#005553',
  'teal-800': '#004140',

  // Cyan variants
  'cyan-100': '#A4F8FF',
  'cyan-200': '#73F4FF',
  'cyan-300': '#3ED2DE',
  'cyan-400': '#29BAC6',
  'cyan-500': '#169CA7',
  'cyan-600': '#007A84',
  'cyan-700': '#115469',
  'cyan-800': '#043C4D',

  // Indigo variants
  'indigo-100': '#CED3EC',
  'indigo-200': '#B4BCE2',
  'indigo-300': '#96A0D5',
  'indigo-400': '#7985CC',
  'indigo-500': '#5D6AC0',
  'indigo-600': '#4C579F',
  'indigo-700': '#3B457B',
  'indigo-800': '#2A3158',

  // Violet variants
  'violet-100': '#B8B1DE',
  'violet-200': '#9F97CA',
  'violet-300': '#8178B3',
  'violet-400': '#7268A6',
  'violet-500': '#5A508A',
  'violet-600': '#473D77',
  'violet-700': '#3D3065',
  'violet-800': '#322558',

  // Purple variants
  'purple-100': '#BB74C8',
  'purple-200': '#AD64BA',
  'purple-300': '#9E51AB',
  'purple-400': '#894195',
  'purple-500': '#6E3078',
  'purple-600': '#5A2164',
  'purple-700': '#4D1A56',
  'purple-800': '#43134C',

  // Magenta variants
  'magenta-100': '#F652B1',
  'magenta-200': '#D93896',
  'magenta-300': '#B81C77',
  'magenta-400': '#9E1364',
  'magenta-500': '#82074F',
  'magenta-600': '#6D0341',
  'magenta-700': '#590134',
  'magenta-800': '#49002B',

  // Pink variants
  'pink-100': '#F3A7BA',
  'pink-200': '#F48BA6',
  'pink-300': '#EB7795',
  'pink-400': '#DB6684',
  'pink-500': '#C55773',
  'pink-600': '#AC4A63',
  'pink-700': '#87374B',
  'pink-800': '#662737',
  'pink-900': '#551928',
  'pink-1000': '#400C19',

  // Red variants
  'red-50': '#FCEDE9',
  'red-100': '#FF7577',
  'red-200': '#E85959',
  'red-300': '#D64242',
  'red-400': '#C82727',
  'red-500': '#AF0003',
  'red-600': '#850003',
  'red-700': '#490001',
  'red-800': '#390001',

  // Orange variants
  'orange-100': '#FF987D',
  'orange-200': '#FF7C5A',
  'orange-300': '#FF673F',
  'orange-400': '#EB522A',
  'orange-500': '#D44621',
  'orange-600': '#9C3B21',
  'orange-700': '#713323',
  'orange-800': '#57291D',

  // Yellow variants
  'yellow-50': '#FEF7EA',
  'yellow-100': '#FFDD9B',
  'yellow-200': '#FFCB66',
  'yellow-300': '#FFC147',
  'yellow-400': '#FFB41E',
  'yellow-500': '#FFA500',
  'yellow-600': '#E98702',
  'yellow-700': '#B35D00',
  'yellow-800': '#804301',

  // Green variants
  'green-50': '#EAF7EE',
  'green-100': '#C9E3A1',
  'green-200': '#ACD46E',
  'green-300': '#98C356',
  'green-400': '#67B52C',
  'green-500': '#50931D',
  'green-600': '#25470C',
  'green-700': '#183105',
  'green-800': '#152707',
};

export type Palette = typeof palette;
